/**
 * WorkspaceNav — the left file + entity navigator primitive.
 *
 * One Studio-grade icon-tab sidebar (Files / Types / Processes) over a single
 * {@link NavSource} adapter. The adapter decouples it from WHERE the data lives
 * (workbench store, server, read-only model, diff changeset) — the SAME nav
 * renders every surface; each just builds a different NavSource.
 *
 * Capability by presence: every file affordance appears iff the source exposes
 * the matching method. A read-only source omits create/rename/delete/duplicate/
 * move (so no context-menu mutations, no drag); revert shows whenever the
 * source can revert. There is no `allowX` flag to drift out of sync with a
 * handler — the method's presence IS the permission.
 *
 * The Files tab is a full file explorer: a collapsible directory tree built
 * from flat paths, with right-click context menu, inline rename, new file /
 * folder, drag-to-move, per-row status badges (M / A), package + space markers,
 * and modules nested under their file. A search box (⌘F) filters every tab.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  useSyncExternalStore,
  type RefObject,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { paletteColor } from "@archlang/scene";
import { aspectColorFor } from "../../aspectColors.js";
import {
  ARCH_EXT,
  ARCHSPACE_EXT,
  MD_EXT,
  hasAncestorManifest,
  hasWorkspaceExt,
  isManifestPath,
  isMarkdownPath,
  manifestSeed,
  resolveNewPath,
} from "@archlang/viewer-core/workspace-file";
import {
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Braces,
  Package,
  File,
  Workflow,
  TriangleAlert,
  Info,
  CircleCheck,
  Eye,
  ListTodo,
  Plus,
} from "lucide-react";
import type { Module, Process, Interface, TypeDef, BaseType } from "@archlang/scene/arch-model";
import { diagnosticText } from "@archlang/engine";
import {
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  EmptyState,
  TabHandle,
  TabHandleStrip,
  type TabHandleBadge,
  type TabBadgeTone,
} from "@archlang/ui";
import { useLocale, useTranslation } from "../../i18n";
import { displayLabel } from "../../displayLabel.js";
import { bareViewLabel } from "../../views/viewRepr.js";
import { typeSelectionKey } from "../../typeKey.js";
import {
  moduleKey,
  BUILTIN_LIBRARY,
  BASE_TYPE_COLOR,
  BASE_TYPE_MARK,
  classifyType,
  filterPreviewableTypes,
  withBaseTypeStubs,
  extractWidgetSpec,
  valueToString,
} from "@archlang/scene/arch-model";
import { iconSvg } from "../../generated/stdlib-widgets/arch.ui/icons.js";
import {
  NodeColoringProvider,
  useNodeColoring,
  type NodeColoring,
} from "../../board/coloring/index.js";
import { ProcessGlyph } from "./icons.js";
import { processKey } from "../../processKey.js";
import { interfaceDisplayName, isAnonymousInterface } from "../../displayNames.js";
import { ContextMenu, type MenuItem as ContextMenuItem } from "../nav/ContextMenu.js";
import { ChangesView } from "../nav/ChangesView.js";
import type { NavDiagnostic, NavFile, NavSource } from "../nav/NavSource.js";
import type { HoverStore } from "../../board/hoverStore.js";

// Narrow viewport (phone). Matches the viewer's mobile breakpoint so the nav's
// own chrome agrees with the surrounding mobile layout. Tabs move to a bottom
// bar; touch sizing comes from `pointer-coarse:` utility variants on the rows.
const NARROW_QUERY = "(max-width: 767px)";
function useIsNarrow(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mq = window.matchMedia(NARROW_QUERY);
      mq.addEventListener("change", cb);
      return () => mq.removeEventListener("change", cb);
    },
    () => (typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(NARROW_QUERY).matches),
    () => false,
  );
}

// `todo` is its own rung, ranked between warning and info: the engine emits it
// for deliberate holes (PROCESS_CALLEE_TODO & friends), which is a different
// thing from an informational note — folding it into `info` hid the category.
type DiagWorst = "error" | "warning" | "todo" | "info" | "ok";
interface DiagSummary {
  readonly error: number;
  readonly warning: number;
  readonly todo: number;
  readonly info: number;
  readonly total: number;
  readonly worst: DiagWorst;
}
function summarizeDiagnostics(diags: readonly NavDiagnostic[]): DiagSummary {
  let error = 0;
  let warning = 0;
  let todo = 0;
  let info = 0;
  for (const d of diags) {
    if (d.severity === "error") error++;
    else if (d.severity === "warning") warning++;
    else if (d.severity === "todo") todo++;
    else info++; // info + hint
  }
  const worst: DiagWorst = error
    ? "error"
    : warning
      ? "warning"
      : todo
        ? "todo"
        : info
          ? "info"
          : "ok";
  return { error, warning, todo, info, total: diags.length, worst };
}
const DIAG_COLOR: Record<DiagWorst, string> = {
  error: "text-red-400",
  warning: "text-amber-400",
  todo: "text-violet-300",
  info: "text-sky-400",
  ok: "text-slate-500",
};
/** Worst severity → the {@link TabHandle} badge tone. */
const DIAG_BADGE_TONE: Record<DiagWorst, TabBadgeTone> = {
  error: "error",
  warning: "warning",
  todo: "todo",
  info: "info",
  ok: "neutral",
};

/** Problems-tab icon — colour-coded by worst severity (red error / amber
 *  warning / violet todo / sky info / muted check when clean). */
function DiagTabIcon({ worst }: { worst: DiagWorst }): ReactElement {
  const cls = cx("h-5 w-5", DIAG_COLOR[worst]);
  if (worst === "ok") return <CircleCheck className={cls} strokeWidth={1.8} aria-hidden="true" />;
  if (worst === "todo") return <ListTodo className={cls} strokeWidth={1.8} aria-hidden="true" />;
  if (worst === "info") return <Info className={cls} strokeWidth={1.8} aria-hidden="true" />;
  return <TriangleAlert className={cls} strokeWidth={1.8} aria-hidden="true" />;
}

const DIAG_ROW_COLOR: Record<NavDiagnostic["severity"], string> = {
  error: "text-red-400",
  warning: "text-amber-400",
  todo: "text-violet-300",
  info: "text-sky-400",
  hint: "text-slate-400",
};

// Stable no-op subscribe so a row without a hover store doesn't re-subscribe
// every render (which would defeat useSyncExternalStore's bookkeeping).
const NO_SUBSCRIBE = (): (() => void) => () => {};

/** Per-row board-hover mirror: re-renders only this row when its board item's
 *  hovered state flips. `id` empty/undefined → never hovered (cheap inert). */
function useNavHovered(store: HoverStore | undefined, id: string | undefined): boolean {
  return useSyncExternalStore(
    store?.subscribe ?? NO_SUBSCRIBE,
    () => !!id && store?.getHoveredId() === id,
    () => false,
  );
}

export type NavView = "files" | "types" | "processes" | "documents" | "views";

/** One host-supplied icon tab (see {@link WorkspaceNavProps.tabs}). */
export interface HostNavTab {
  readonly id: string;
  /** Accessible name + tooltip. */
  readonly label: string;
  readonly icon: ReactNode;
  /**
   * Genuinely unavailable (no permission / not loaded yet) — NOT "empty". An
   * empty tab stays a normal clickable handle and says so in its panel body.
   */
  readonly disabled?: boolean;
  /** Tooltip override (e.g. why a tab is disabled). */
  readonly title?: string;
  /** Complication: a count pill / dot on the handle (see {@link TabHandle}). */
  readonly badge?: TabHandleBadge | null;
  /** Complication: an arbitrary corner node on the handle. */
  readonly overlay?: ReactNode;
}

export interface WorkspaceNavProps {
  readonly source: NavSource;
  readonly className?: string;
  /**
   * Persist expansion / tab state to localStorage under this key so the tree
   * survives a reload. Omit for ephemeral surfaces (embeds, tests).
   */
  readonly persistKey?: string;
  /** Extra header content (right-aligned) — e.g. Studio's theme toggle. */
  readonly headerExtra?: ReactNode;
  /**
   * Controlled view. When set, the internal tab strip is hidden and the host
   * owns which list shows (the viewer's mode tabs drive this). Uncontrolled
   * (undefined) renders the built-in Files/Types/Processes tabs.
   */
  readonly view?: NavView;
  /**
   * Host-defined icon tabs rendered in the nav's tab strip (same look as the
   * built-in ones) — the viewer mounts its Board/Types/Process/Docs modes
   * here. Replaces the internal tabs; pair with `view` to map the active tab
   * to a list. Selection stays with the host.
   */
  readonly tabs?: {
    readonly items: readonly HostNavTab[];
    readonly active: string;
    readonly onSelect: (id: string) => void;
  };
  /**
   * Panel bodies for host-supplied nav SECTIONS (the `navSections` plugin seam),
   * keyed by their tab id. When the active host tab (`tabs.active`) has an entry
   * here, its node replaces the built-in list body — so a plugin section renders
   * its own panel while riding the same tab strip as the built-in tabs.
   */
  readonly sectionPanels?: ReadonlyMap<string, ReactNode>;
  /** External ref to the search input (the viewer focuses it on tab click /
   *  board type-to-search). */
  readonly searchInputRef?: RefObject<HTMLInputElement | null>;
  /** Bind ⌘F/Ctrl+F to focus the nav search (default true). Hosts that own
   *  a global ⌘F (the viewer's search overlay) turn this off. */
  readonly hotkey?: boolean;
  /**
   * Active board node-coloring (aspect/label legend or diff status). When set,
   * module rows pick up the same per-node colour the board shows, so the nav
   * stays in sync with the current view's legend. Omit for plain type colours.
   */
  readonly coloring?: NodeColoring | null;
}

// ---------------------------------------------------------------------------
// Persisted tree state (per persistKey)
// ---------------------------------------------------------------------------

interface PersistedNavState {
  readonly view?: NavView;
  readonly expandedDirs?: readonly string[];
  readonly collapsedFiles?: readonly string[];
  readonly collapsedGroups?: readonly string[];
  /** Module rows whose sub-rows (interfaces + children) are expanded —
   *  modules default COLLAPSED (qualified paths). */
  readonly expandedModules?: readonly string[];
}

function loadNavState(key: string | undefined): PersistedNavState {
  if (!key || typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(`archlang.nav.${key}`);
    return raw ? (JSON.parse(raw) as PersistedNavState) : {};
  } catch {
    return {};
  }
}

const cx = (...a: (string | false | null | undefined)[]): string =>
  a.filter(Boolean).join(" ");

const LOCAL_LIBRARY_LABEL = "(this package)";
const UNKNOWN_FILE = "(unknown)";

function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(i + 1) : path;
}
/**
 * DISPLAY-decode a file label. VFS keys are URL-encoded per segment for
 * storage/routing (a nested "services/api.arch" whose "/" collapsed to `%2F`,
 * or a legacy double-encoded `%252F` from re-encoding an already-encoded key),
 * so decode for the label — the storage key stays untouched. Bounded, and
 * `decodeURIComponent` on a malformed value throws → fall back to the raw text.
 */
function decodeLabel(label: string): string {
  let out = label;
  for (let i = 0; i < 3 && out.includes("%"); i++) {
    try {
      const dec = decodeURIComponent(out);
      if (dec === out) break;
      out = dec;
    } catch {
      break;
    }
  }
  return out;
}
/** Bare directory of a (possibly "/"-prefixed) path: "" = root. */
function bareDir(path: string): string {
  const bare = path.replace(/^\/+/, "");
  const i = bare.lastIndexOf("/");
  return i >= 0 ? bare.slice(0, i) : "";
}
function joinDir(dir: string, name: string): string {
  return dir ? `${dir}/${name}` : name;
}

/** Processes display owner-qualified (`Platform.Checkout`) like the viewer. */
function labelForProcess(p: Process): string {
  return p.ownerPath && p.ownerPath.length > 0
    ? `${p.ownerPath.join(".")}.${p.name}`
    : p.name;
}

// Subtype forest for the Types tab — same shape as the viewer's TypesPane:
// a type roots its library's tree unless its parent lives in the same library.
interface TypeNode {
  readonly type: TypeDef;
  readonly children: readonly TypeNode[];
}

function countTypeNodes(nodes: readonly TypeNode[]): number {
  let n = 0;
  for (const node of nodes) n += 1 + countTypeNodes(node.children);
  return n;
}

function buildTypeForest(
  list: readonly TypeDef[],
  baseTypeByName: ReadonlyMap<string, BaseType>,
): TypeNode[] {
  const names = new Set(list.map((t) => t.name));
  const childrenOf = new Map<string, TypeDef[]>();
  const roots: TypeDef[] = [];
  for (const t of list) {
    const parent = t.parentType;
    if (parent && parent !== t.name && names.has(parent)) {
      const arr = childrenOf.get(parent);
      if (arr) arr.push(t);
      else childrenOf.set(parent, [t]);
    } else {
      roots.push(t);
    }
  }
  // Siblings order by base kind first — all modules, then all interfaces, then
  // anything else — and alphabetically within a kind.
  const kindRank = (t: TypeDef): number => {
    const bt = baseTypeByName.get(t.name);
    return bt === "module" ? 0 : bt === "interface" ? 1 : 2;
  };
  const sortSiblings = (arr: TypeDef[]): TypeDef[] =>
    [...arr].sort((a, b) => kindRank(a) - kindRank(b) || a.name.localeCompare(b.name));
  const toNode = (t: TypeDef): TypeNode => ({
    type: t,
    children: sortSiblings(childrenOf.get(t.name) ?? []).map(toNode),
  });
  return sortSiblings(roots).map(toNode);
}

/** Canonical file key — model sourceFiles and nav paths may disagree on the
 *  leading slash (workbench VFS is "/"-prefixed, Studio repo paths are bare). */
function fileKey(path: string): string {
  return path.startsWith("/") ? path : "/" + path;
}

function groupBySourceFile<T extends { sourceFile?: string }>(
  items: readonly T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const it of items) {
    if (!it.sourceFile) continue;
    const key = fileKey(it.sourceFile);
    const list = map.get(key);
    if (list) list.push(it);
    else map.set(key, [it]);
  }
  return map;
}

// Externalised children (declared `in Parent` from a DIFFERENT file): the
// real row nests under its parent; a grayed stub row marks the declaration
// file, each side carrying a jump icon to its counterpart (legacy
// ModulesPane parity).
interface ExternStub {
  readonly module: Module;
  /** Resolved-tree path of the parent, e.g. `Platform`. */
  readonly parentPathLabel: string;
  /** Qualified path of the child (row-key base for both sides). */
  readonly path: string;
  /** File (canonical key) whose group renders the REAL row. */
  readonly realFile: string;
}

interface ExternIndex {
  /** Stub rows per declaration file (canonical key). */
  readonly stubsByFile: ReadonlyMap<string, readonly ExternStub[]>;
  /** Real-row qualified path → its declaration file (canonical key). */
  readonly externByPath: ReadonlyMap<string, string>;
}

function buildExternIndex(roots: readonly Module[]): ExternIndex {
  const stubsByFile = new Map<string, ExternStub[]>();
  const externByPath = new Map<string, string>();
  const visit = (
    m: Module,
    path: string,
    groupFile: string | null,
    ownFile: string | null,
  ): void => {
    for (const c of m.children) {
      const cPath = `${path}.${c.name}`;
      const cFile = c.sourceFile ? fileKey(c.sourceFile) : ownFile;
      if (cFile && ownFile && groupFile && cFile !== ownFile) {
        const arr = stubsByFile.get(cFile);
        const stub: ExternStub = {
          module: c,
          parentPathLabel: path,
          path: cPath,
          realFile: groupFile,
        };
        if (arr) arr.push(stub);
        else stubsByFile.set(cFile, [stub]);
        externByPath.set(cPath, cFile);
      }
      visit(c, cPath, groupFile, cFile);
    }
  };
  for (const m of roots) {
    const gf = m.sourceFile ? fileKey(m.sourceFile) : null;
    visit(m, m.name, gf, gf);
  }
  for (const arr of stubsByFile.values()) {
    arr.sort((a, b) => a.path.localeCompare(b.path));
  }
  return { stubsByFile, externByPath };
}

// Process tree: processes nested under their owning module (by `ownerPath`),
// the owner modules rendered as a tree (like the files tab). Free-standing
// processes collect at the root.
interface ProcTreeNode {
  readonly key: string; // qualified module path, e.g. "Platform.Orders"
  readonly name: string; // module short name
  readonly module: Module | null; // resolved owner (icon + selection)
  readonly procs: Process[];
  readonly children: Map<string, ProcTreeNode>;
}

function buildProcessTree(
  modules: readonly Module[],
  processes: readonly Process[],
): { roots: Process[]; nodes: ProcTreeNode[] } {
  // Roots are registered under a SPACE-prefixed path (v0.11 D5): two spaces
  // may each own the same module path, and the grouping below prefixes owner
  // segments with the process's landed space — one shared bare path would
  // last-wins both spaces' processes into one mixed node.
  const moduleByPath = new Map<string, Module>();
  const walkMods = (m: Module, prefix: string): void => {
    const path = prefix ? `${prefix}.${m.name}` : m.name;
    moduleByPath.set(path, m);
    for (const c of m.children) walkMods(c, path);
  };
  for (const m of modules) walkMods(m, m.space ?? "");

  const root: ProcTreeNode = { key: "", name: "", module: null, procs: [], children: new Map() };
  const roots: Process[] = [];
  const ensure = (segs: readonly string[]): ProcTreeNode => {
    let cur = root;
    let acc = "";
    for (const seg of segs) {
      acc = acc ? `${acc}.${seg}` : seg;
      let child = cur.children.get(seg);
      if (!child) {
        child = { key: acc, name: seg, module: moduleByPath.get(acc) ?? null, procs: [], children: new Map() };
        cur.children.set(seg, child);
      }
      cur = child;
    }
    return cur;
  };
  for (const p of processes) {
    const owner = p.ownerPath ?? [];
    // A spaced process groups under its space node (D5): free twins across
    // spaces would otherwise render as indistinguishable same-named rows, and
    // module-owned twins would merge into one bare-path group.
    const segs = p.space ? [p.space, ...owner] : owner;
    if (segs.length === 0) roots.push(p);
    else ensure(segs).procs.push(p);
  }
  const sortNode = (n: ProcTreeNode): void => {
    n.procs.sort((a, b) => a.name.localeCompare(b.name));
    for (const c of n.children.values()) sortNode(c);
  };
  sortNode(root);
  roots.sort((a, b) => a.name.localeCompare(b.name));
  return {
    roots,
    nodes: [...root.children.values()].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

// ---------------------------------------------------------------------------
// Directory tree (bare dir paths internally; files keep their original path)
// ---------------------------------------------------------------------------

interface DirNode {
  readonly name: string;
  readonly path: string; // bare dir path, "" = root
  readonly dirs: Map<string, DirNode>;
  readonly files: { readonly name: string; readonly file: NavFile }[];
}

function buildTree(files: readonly NavFile[], pendingDirs: readonly string[]): DirNode {
  const root: DirNode = { name: "", path: "", dirs: new Map(), files: [] };
  const ensureDir = (segments: readonly string[]): DirNode => {
    let cur = root;
    let acc = "";
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      let child = cur.dirs.get(seg);
      if (!child) {
        child = { name: seg, path: acc, dirs: new Map(), files: [] };
        cur.dirs.set(seg, child);
      }
      cur = child;
    }
    return cur;
  };
  for (const dir of pendingDirs) {
    if (dir) ensureDir(dir.split("/"));
  }
  for (const file of files) {
    const bare = file.path.replace(/^\/+/, "");
    const segs = bare.split("/");
    const name = segs.pop()!;
    ensureDir(segs).files.push({ name, file });
  }
  return root;
}

type MenuTarget =
  | { kind: "file"; file: NavFile }
  | { kind: "dir" | "root"; path: string }
  | { kind: "module"; id: string }
  | { kind: "process"; id: string };
type Editing =
  | { mode: "rename-file" | "rename-dir"; path: string }
  | { mode: "new-file"; dir: string; ext: string; initial: string }
  | { mode: "new-dir"; dir: string };

/**
 * The nav's inline name editor — new file, new folder, rename.
 *
 * It OWNS focus for its first task tick. React deletes a closing menu's subtree
 * during the mutation phase, so floating-ui's focus manager samples `<body>` as
 * the active element (this input auto-focuses later, in the layout phase), its
 * "focus moved elsewhere, leave it" guard doesn't fire, and it returns focus to
 * the menu button one microtask after mount. That blur arrived on an empty
 * input, read as "cancel", and silently dropped every `+ → New file`. So a blur
 * that (a) lands before the input has settled and (b) really moved focus away is
 * a THEFT, not a user action: take focus back.
 *
 * Commit/cancel are explicit — Enter commits, Escape cancels, a genuine blur
 * commits what the USER typed. Nothing is inferred from an empty value, and an
 * UNTOUCHED value isn't user intent either: leaving a prefilled editor (opening
 * the + menu again, clicking a row) walked away from the edit, so committing it
 * minted a file nobody asked for — `package.archspace` from the manifest
 * entry's "package" prefill.
 *
 * The caller must give it a `key` covering everything it snapshots on mount
 * (dir, ext, initial): two edits that differ only in file type reconcile onto
 * one instance otherwise, and it keeps the old prefill AND the already-fired
 * settle guard — which put the very focus-theft bug above straight back on the
 * second use of the menu.
 */
function InlineNameInput({
  initial,
  pristine,
  wasEdited,
  ext,
  label,
  validate,
  onDraft,
  onCommit,
  onCancel,
}: {
  readonly initial: string;
  /** What this edit was BORN with — the prefill/current name, never the draft.
   *  The blur handler's "untouched, user walked away → discard" test compares
   *  against this. Comparing against `initial` was the same value as the typed
   *  text whenever the parent had re-rendered (it re-seeds `initial` from the
   *  draft so a remount doesn't lose typing), so every blur after a re-render —
   *  the ordinary "name it, then click elsewhere" gesture — silently discarded
   *  the name. */
  readonly pristine: string;
  /** Did the user ever type in THIS edit? Asked of the parent, not tracked
   *  here: a search filter remounts this component (the draft restores the
   *  text, a local ref would reset), and the blur guard would fall back to
   *  comparing the value against `pristine` — which reads as "untouched" for
   *  anyone who types their way back to a prefill. The manifest arm is the one
   *  prefilled arm, so that silently discarded exactly the file it was
   *  hardest to notice losing. */
  readonly wasEdited: () => boolean;
  readonly ext: string | null;
  readonly label: string;
  /** Is what's typed a name the workspace can hold? Drives the invalid style —
   *  a refused name must SAY so, not just fail to appear. */
  readonly validate: (value: string) => boolean;
  /** Every keystroke, so the parent can re-seed this input if it remounts for
   *  an unrelated reason (a search query flips the tree to flat results). */
  readonly onDraft: (value: string) => void;
  /** Returns false when the name was REFUSED — the editor then stays open and
   *  keeps focus so the user can fix it in place. */
  readonly onCommit: (value: string) => boolean;
  readonly onCancel: () => void;
}): ReactElement {
  const [invalid, setInvalid] = useState(false);
  // Settles after the current task — later than any microtask-scheduled steal.
  const settled = useRef(false);
  // Enter/Escape already resolved the edit; the blur they cause is a no-op.
  const done = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      settled.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex items-center gap-0.5">
      <input
        autoFocus
        defaultValue={initial}
        aria-label={label}
        aria-invalid={invalid || undefined}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const v = e.currentTarget.value;
          onDraft(v);
          setInvalid(v.trim() !== "" && !validate(v));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const el = e.currentTarget;
            if (onCommit(el.value)) done.current = true;
            else queueMicrotask(() => el.isConnected && el.focus());
          } else if (e.key === "Escape") {
            done.current = true;
            onCancel();
          }
        }}
        onBlur={(e) => {
          if (done.current) return;
          const el = e.currentTarget;
          if (!settled.current && el.isConnected && document.activeElement !== el) {
            // A microtask, not a straight `el.focus()`: the thief's `focus()`
            // is still mid-flight (the blur fires while the active element is
            // momentarily `<body>`), so focusing synchronously here is undone
            // the instant it resumes.
            queueMicrotask(() => {
              if (el.isConnected) el.focus();
            });
            return;
          }
          // Walked away from an untouched editor → discard, don't create.
          if (!wasEdited() && el.value.trim() === pristine.trim()) {
            done.current = true;
            onCancel();
            return;
          }
          if (onCommit(el.value)) {
            done.current = true;
            return;
          }
          // Refused: `done` must stay false or the corrected name could never
          // be committed. Focus is NOT taken back — the user just clicked
          // somewhere, and re-grabbing it makes an invalid name a trap you can
          // only leave with Escape. The editor stays open, red, with the text.
        }}
        className={cx(
          "min-w-0 flex-1 rounded border bg-input px-1 py-0.5 text-slate-100 outline-none",
          invalid ? "border-red-500" : "border-arch-500",
        )}
      />
      {ext && <span className="shrink-0 select-none text-xs text-slate-500">{ext}</span>}
    </div>
  );
}

export function WorkspaceNav({
  source,
  className,
  persistKey,
  headerExtra,
  view: controlledView,
  tabs,
  sectionPanels,
  searchInputRef,
  hotkey = true,
  coloring,
}: WorkspaceNavProps): ReactElement {
  // A plugin nav section active → its panel replaces the built-in list body.
  const activeSectionPanel = tabs ? sectionPanels?.get(tabs.active) : undefined;
  const model = source.model();
  const sel = source.selection();
  const selectedViewName = source.selectedViewName?.() ?? null;
  const files = source.files();
  const packageDirs = source.packageDirs?.();
  const spaceDirs = source.spaceDirs?.();
  // Diff mode: a non-null changeset swaps the live-model tabs for a changes tree.
  const diff = source.diff?.() ?? null;
  const narrow = useIsNarrow();
  // Diagnostics → an optional Problems tab. `diagOpen` overrides the body
  // (works for both host-tab and built-in-tab surfaces).
  const diagnostics = source.diagnostics?.() ?? null;
  const { locale } = useLocale();
  const { t } = useTranslation("nav");
  const diagSummary = useMemo(
    () => (diagnostics ? summarizeDiagnostics(diagnostics) : null),
    [diagnostics],
  );
  const [diagOpen, setDiagOpen] = useState(false);

  const modulesByFile = useMemo(() => groupBySourceFile(model?.modules ?? []), [model]);
  // Anonymous (empty-name) processes are elided everywhere in the nav — a
  // module body's bare/misparsed statement can yield a nameless process with
  // no backing declaration; it must not render as a phantom entry. Mirrors the
  // `processes` (Processes-tab) filter below.
  const processesByFile = useMemo(
    () => groupBySourceFile((model?.processes ?? []).filter((p) => p.name.trim() !== "")),
    [model],
  );
  const viewsByFile = useMemo(() => groupBySourceFile(source.views?.() ?? []), [source]);
  const externIndex = useMemo(() => buildExternIndex(model?.modules ?? []), [model]);
  const processes = useMemo(
    () =>
      [...(model?.processes ?? [])]
        .filter((p) => p.name.trim() !== "")
        .sort((a, b) => labelForProcess(a).localeCompare(labelForProcess(b))),
    [model],
  );
  // Processes tab: grouped by source file, each file holding the owner-module
  // tree (processes nested under their module, default expanded).
  const processGroups = useMemo(() => {
    const byFile = new Map<string, Process[]>();
    for (const p of processes) {
      const f = p.sourceFile ? fileKey(p.sourceFile) : UNKNOWN_FILE;
      const arr = byFile.get(f);
      if (arr) arr.push(p);
      else byFile.set(f, [p]);
    }
    return [...byFile.entries()]
      .sort(([a], [b]) => (a === UNKNOWN_FILE ? 1 : b === UNKNOWN_FILE ? -1 : a.localeCompare(b)))
      .map(([file, procs]) => ({ file, ...buildProcessTree(model?.modules ?? [], procs) }));
  }, [model, processes]);
  const anyProcesses = processGroups.length > 0;
  // Types tab data — viewer-TypesPane parity: base-kind stubs + previewable
  // filter, a baseType badge per type, and a subtype forest per library,
  // libraries ranked builtin → this-package → others.
  const { typeGroups, baseTypeByName, typeIconByName, augmentedTypes } = useMemo(() => {
    const augmented = filterPreviewableTypes(withBaseTypeStubs(model?.types ?? []));
    const byName = new Map<string, TypeDef>();
    for (const t of augmented) byName.set(t.name, t);
    const baseTypeByName = new Map<string, BaseType>();
    for (const t of augmented) baseTypeByName.set(t.name, classifyType(t, byName).baseType);
    // Resolve each type's widget icon up its parent chain (a subtype inherits
    // its parent's icon unless it sets its own), so the types tab shows the
    // same glyph the instances render. Module-kind types with no icon anywhere
    // fall back to the builtin `module` glyph.
    const typeIconByName = new Map<string, string>();
    const resolveTypeIcon = (name: string, seen: Set<string>): string | undefined => {
      if (seen.has(name)) return undefined;
      seen.add(name);
      const t = byName.get(name);
      if (!t) return undefined;
      const own = typeOwnIconName(t);
      if (own) return own;
      return t.parentType ? resolveTypeIcon(t.parentType, seen) : undefined;
    };
    for (const t of augmented) {
      const resolved = resolveTypeIcon(t.name, new Set());
      const fallback = baseTypeByName.get(t.name) === "module" ? "module" : "";
      typeIconByName.set(t.name, resolved ?? fallback);
    }
    const m = new Map<string, TypeDef[]>();
    for (const t of augmented) {
      const lib = t.library ?? LOCAL_LIBRARY_LABEL;
      const arr = m.get(lib);
      if (arr) arr.push(t);
      else m.set(lib, [t]);
    }
    const rank = (k: string): number =>
      k === BUILTIN_LIBRARY ? 0 : k === LOCAL_LIBRARY_LABEL ? 1 : 2;
    const typeGroups = [...m.entries()]
      .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
      .map(([lib, list]) => [lib, buildTypeForest(list, baseTypeByName)] as const);
    return {
      typeGroups,
      baseTypeByName,
      typeIconByName,
      augmentedTypes: [...augmented].sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [model]);

  // Capability by presence.
  const canCreate = !!source.create;
  const canRename = !!source.rename;
  const canDelete = !!source.delete;
  const canDuplicate = !!source.duplicate;
  const canMove = !!source.move;
  const canRevert = !!source.revert;
  // "read-only" badge = editable-but-not-written-back surfaces (they can
  // revert local edits). A purely read-only source (viewer) gets no badge —
  // nothing suggests editing in the first place.
  const readonly = !canCreate && canRevert;

  // New paths follow whatever convention the existing files use ("/"-prefixed
  // VFS paths, or bare). Empty workspace → default to the VFS "/" convention.
  const lead = files.length === 0 ? "/" : files[0]!.path.startsWith("/") ? "/" : "";
  const fullPath = (dir: string, name: string): string => lead + joinDir(dir, name);

  const persisted = useRef<PersistedNavState | null>(null);
  if (persisted.current === null) persisted.current = loadNavState(persistKey);
  const [uncontrolledView, setView] = useState<NavView>(() =>
    persisted.current!.view === "types" || persisted.current!.view === "processes"
      ? persisted.current!.view
      : "files",
  );
  // Controlled view (viewer mode tabs) wins; otherwise the internal tabs.
  const view = controlledView ?? uncontrolledView;
  const [collapsedFiles, setCollapsedFiles] = useState<ReadonlySet<string>>(
    () => new Set(persisted.current!.collapsedFiles ?? []),
  );
  const [expandedDirs, setExpandedDirs] = useState<ReadonlySet<string>>(
    () => new Set(persisted.current!.expandedDirs ?? []),
  );
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    () => new Set(persisted.current!.collapsedGroups ?? []),
  );
  // Modules default collapsed (interfaces + nested children hidden) — this
  // set tracks the ones the user explicitly opened.
  const [expandedModules, setExpandedModules] = useState<ReadonlySet<string>>(
    () => new Set(persisted.current!.expandedModules ?? []),
  );
  const [pendingDirs, setPendingDirs] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<Editing | null>(null);
  // What the user has typed into the open editor. The tree unmounts the input
  // for reasons that have nothing to do with the edit (a search query switches
  // to flat results), and an uncontrolled input silently reverts to its
  // prefill on remount — the typed name just disappeared. A ref, not state:
  // per-keystroke state here would re-render the whole tree.
  const draftRef = useRef<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; target: MenuTarget } | null>(null);
  const [dragSrc, setDragSrc] = useState<string | null>(null);
  const [dropDir, setDropDir] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Persist tree/tab state per surface (skipped without a persistKey).
  useEffect(() => {
    if (!persistKey || typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(
        `archlang.nav.${persistKey}`,
        JSON.stringify({
          view,
          expandedDirs: [...expandedDirs],
          collapsedFiles: [...collapsedFiles],
          collapsedGroups: [...collapsedGroups],
          expandedModules: [...expandedModules],
        } satisfies PersistedNavState),
      );
    } catch {
      // Quota / private-mode failures are non-fatal — the tree just resets.
    }
  }, [persistKey, view, expandedDirs, collapsedFiles, collapsedGroups, expandedModules]);

  // Auto-expand ancestors of the active file so it stays visible.
  useEffect(() => {
    const active = sel.activeFile;
    if (!active) return;
    const dir = bareDir(active);
    if (!dir) return;
    setExpandedDirs((prev) => {
      const next = new Set(prev);
      let acc = "";
      for (const seg of dir.split("/")) {
        acc = acc ? `${acc}/${seg}` : seg;
        next.add(acc);
      }
      return next;
    });
  }, [sel.activeFile]);

  // --- externalised-child jumps (stub ↔ real counterpart) -------------------

  const rowRefs = useRef(new Map<string, HTMLElement | null>());
  const registerRow =
    (key: string) =>
    (el: HTMLElement | null): void => {
      if (el) rowRefs.current.set(key, el);
      else rowRefs.current.delete(key);
    };

  /** Expand the target file's ancestors + un-collapse it, then scroll the
   *  counterpart row into view (double rAF: let the expansion render first). */
  const revealRow = (file: string, rowKey: string): void => {
    const bare = file.replace(/^\/+/, "");
    const dir = bareDir(bare);
    if (dir) {
      setExpandedDirs((prev) => {
        const next = new Set(prev);
        let acc = "";
        for (const seg of dir.split("/")) {
          acc = acc ? `${acc}/${seg}` : seg;
          next.add(acc);
        }
        return next;
      });
    }
    setCollapsedFiles((prev) => {
      if (![...prev].some((p) => fileKey(p) === fileKey(file))) return prev;
      return new Set([...prev].filter((p) => fileKey(p) !== fileKey(file)));
    });
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        rowRefs.current.get(rowKey)?.scrollIntoView?.({ behavior: "smooth", block: "center" }),
      ),
    );
  };

  const jumpToStub = (path: string): void => {
    const stubFile = externIndex.externByPath.get(path);
    if (stubFile) revealRow(stubFile, `stub:${path}`);
  };
  const jumpToReal = (stub: ExternStub): void => {
    expandModuleAncestors(stub.path);
    revealRow(stub.realFile, `real:${stub.path}`);
  };

  // Board double-click → scroll the matching module row into view (a single
  // board click only selects; the row may stay off-screen). Nonce-gated so
  // repeated opens of the same module still scroll.
  const moduleInfoByKey = useMemo(() => {
    const map = new Map<string, { file: string; path: string }>();
    for (const [file, mods] of modulesByFile) {
      const walk = (m: Module, path: string): void => {
        map.set(moduleKey(m), { file, path });
        for (const c of m.children) walk(c, `${path}.${c.name}`);
      };
      for (const m of mods) walk(m, m.name);
    }
    return map;
  }, [modulesByFile]);
  const navRevealReq = source.navReveal?.() ?? null;
  const lastNavRevealNonce = useRef(0);
  useEffect(() => {
    if (!navRevealReq || navRevealReq.nonce === lastNavRevealNonce.current) return;
    lastNavRevealNonce.current = navRevealReq.nonce;
    const info = moduleInfoByKey.get(navRevealReq.key);
    if (info) {
      expandModuleAncestors(info.path);
      revealRow(info.file, `mod:${navRevealReq.key}`);
    }
    // revealRow/expandModuleAncestors are stable-per-render closures; the
    // nonce guard makes extra runs no-ops, so they're excluded from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navRevealReq, moduleInfoByKey]);

  // Documents view = the same file tree, filtered to markdown only (the
  // universal docs perspective). Files view shows everything.
  const isDocs = view === "documents";
  const viewFiles = useMemo(
    () => (isDocs ? files.filter((f) => isMarkdownPath(f.path)) : files),
    [files, isDocs],
  );
  const tree = useMemo(() => buildTree(viewFiles, [...pendingDirs]), [viewFiles, pendingDirs]);
  const anyDirty = files.some((f) => f.status);

  const toggleSet = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  };
  const toggleFile = (path: string): void => setCollapsedFiles((s) => toggleSet(s, path));
  const toggleDir = (path: string): void => setExpandedDirs((s) => toggleSet(s, path));
  const toggleModule = (path: string): void => setExpandedModules((s) => toggleSet(s, path));
  // Process-tree owner modules default EXPANDED (the tab is process-focused);
  // this set tracks the ones the user collapsed.
  const [collapsedProcModules, setCollapsedProcModules] = useState<ReadonlySet<string>>(new Set());
  const toggleProcModule = (key: string): void =>
    setCollapsedProcModules((s) => toggleSet(s, key));
  // Per-file process groups, default expanded; this set tracks collapsed files.
  const [collapsedProcFiles, setCollapsedProcFiles] = useState<ReadonlySet<string>>(new Set());
  const toggleProcFile = (key: string): void =>
    setCollapsedProcFiles((s) => toggleSet(s, key));
  /** Open every ancestor module of a qualified path (jump targets). */
  const expandModuleAncestors = (path: string): void => {
    const segs = path.split(".");
    if (segs.length < 2) return;
    setExpandedModules((prev) => {
      const next = new Set(prev);
      let acc = segs[0]!;
      next.add(acc);
      for (let i = 1; i < segs.length - 1; i++) {
        acc = `${acc}.${segs[i]}`;
        next.add(acc);
      }
      return next;
    });
  };
  /** Expand `path` AND every directory above it — a nested dir revealed inside
   *  a collapsed ancestor is still invisible, which is the same "nothing
   *  happened" the create flow exists to avoid. */
  const expandDir = (path: string): void =>
    setExpandedDirs((s) => {
      if (!path) return s;
      const want: string[] = [];
      for (let i = path.indexOf("/"); i >= 0; i = path.indexOf("/", i + 1))
        want.push(path.slice(0, i));
      want.push(path);
      if (want.every((d) => s.has(d))) return s;
      const next = new Set(s);
      for (const d of want) next.add(d);
      return next;
    });
  const toggleGroup = (key: string): void => setCollapsedGroups((s) => toggleSet(s, key));

  // Universal grammar: single click = select (host toggles on re-click),
  // double click = bring into view / open. Each open* falls back to
  // select + editor reveal when the source doesn't provide it.
  const onModule = (m: Module): void => source.selectModule(moduleKey(m));
  const onModuleOpen = (m: Module): void => {
    if (source.openModule) source.openModule(moduleKey(m));
    else {
      source.selectModule(moduleKey(m));
      source.reveal(m.name);
    }
  };
  const onType = (name: string): void => {
    if (source.selectType) source.selectType(name);
    else source.reveal(name);
  };
  const onTypeOpen = (name: string): void => {
    if (source.openType) source.openType(name);
    else {
      source.selectType?.(name);
      source.reveal(name);
    }
  };
  const onProcess = (p: Process): void => {
    if (source.selectProcess) source.selectProcess(processKey(p));
    else source.reveal(p.name);
  };
  const onProcessOpen = (p: Process): void => {
    if (source.openProcess) source.openProcess(processKey(p));
    else {
      source.selectProcess?.(processKey(p));
      source.reveal(p.name);
    }
  };
  const onInterface = (m: Module, name: string): void => {
    if (source.selectInterface) source.selectInterface(`${moduleKey(m)}.${name}`);
    else source.reveal(name);
  };
  const onInterfaceOpen = (m: Module, name: string): void => {
    if (source.openInterface) source.openInterface(`${moduleKey(m)}.${name}`);
    else {
      source.selectInterface?.(`${moduleKey(m)}.${name}`);
      source.reveal(name);
    }
  };
  // Board ↔ nav hover link (inert when the source doesn't wire it).
  const hoverStore = source.hoverStore;

  // --- mutations -----------------------------------------------------------

  const descendants = (dir: string): NavFile[] =>
    files.filter((f) => {
      const bare = f.path.replace(/^\/+/, "");
      return bare === dir || bare.startsWith(dir + "/");
    });

  const startNewFile = (dir: string, ext = ARCH_EXT, initial = ""): void => {
    expandDir(dir);
    setView("files");
    draftRef.current = null;
    setEditing({ mode: "new-file", dir, ext, initial });
  };
  const startNewDir = (dir: string): void => {
    expandDir(dir);
    setView("files");
    draftRef.current = null;
    setEditing({ mode: "new-dir", dir });
  };

  /** The name a typed value resolves to for `editing`, or `null` when the
   *  workspace can't hold it. ONE law for every arm — the create path grew
   *  `resolveNewPath` first and its siblings kept minting `/src/../evil.arch`
   *  and inert `B.ARCH` through the raw joiner. */
  const resolveEdit = (e: Editing, value: string): string | null => {
    const target = resolvePath(e, value);
    return target !== null && collides(e, target) ? null : target;
  };

  /** Bare paths that already exist, as files and as directories. A directory
   *  exists because some file lives under it (or it is pending). */
  const existing = (): { fileAt: Set<string>; dirAt: Set<string> } => {
    const fileAt = new Set<string>();
    const dirAt = new Set<string>();
    const addWithAncestors = (path: string, self: Set<string>): void => {
      self.add(path);
      // Every ancestor is itself a directory that exists. Pending dirs were
      // stored VERBATIM, so after "New folder p/q" the row `p` rendered while
      // `p` was absent from `dirAt` — and a second folder named `p` was
      // accepted as though nothing were there.
      for (let i = path.indexOf("/"); i >= 0; i = path.indexOf("/", i + 1))
        dirAt.add(path.slice(0, i));
    };
    for (const d of pendingDirs) addWithAncestors(d, dirAt);
    for (const f of files) addWithAncestors(f.path.replace(/^\/+/, ""), fileAt);
    return { fileAt, dirAt };
  };

  /** Is some ANCESTOR of `target` an existing file? Nothing can live under a
   *  file. Every arm tested the exact target only, so `a.arch/sub/b.arch` was
   *  accepted and the tree then rendered two rows named `a.arch` — one file,
   *  one dir: the exact symptom this whole law exists to prevent, one level up. */
  const underAFile = (target: string, fileAt: ReadonlySet<string>): boolean => {
    for (let i = target.indexOf("/"); i >= 0; i = target.indexOf("/", i + 1))
      if (fileAt.has(target.slice(0, i))) return true;
    return false;
  };

  /** Is `target` a place this edit cannot land? A path is EITHER a file or a
   *  directory, and the nav used to let either overwrite the other's slot:
   *  creating a file named like a dir rendered that name twice (once per role),
   *  and a rename onto an occupied name fired a `source.rename` the store
   *  refuses with a bare `return` — a dead button with no explanation. Landing
   *  ON an existing FILE stays legal for `new-file`: the store reveals it,
   *  which is the "I meant that one" gesture, not a clobber. */
  const collides = (e: Editing, target: string): boolean => {
    const { fileAt, dirAt } = existing();
    if (underAFile(target, fileAt)) return true;
    if (e.mode === "new-file") {
      // The CURATED manifest entry mints the one basename every loader reads.
      // Overtyping its prefilled stem produced `mypkg.archspace`: created
      // unseeded (the seed keys on `isManifestPath`) and read by nothing, with
      // no hint why. The free-form "Other file…" arm (ext "") is untouched —
      // that is where an arbitrary `.archspace` may still be minted.
      if (e.ext === ARCHSPACE_EXT && !isManifestPath(target)) return true;
      return dirAt.has(target);
    }
    // A folder duplicating an existing folder was the ONE arm that accepted its
    // own role's duplicate: it consulted the file set only.
    if (e.mode === "new-dir") return fileAt.has(target) || dirAt.has(target);
    if (e.mode === "rename-file") {
      const self = e.path.replace(/^\/+/, "");
      return target !== self && (fileAt.has(target) || dirAt.has(target));
    }
    // rename-dir: merging two trees is never what a rename meant — and it
    // half-applied, since the store refuses only the COLLIDING files and moves
    // the rest, leaving the source dir alive after a "successful" rename.
    if (target === e.path) return false;
    // `mv dir dir/sub` — a dir cannot swallow itself. Compared by SEGMENT, not
    // by string prefix: "one" → "onex/inner" shares a prefix but is a perfectly
    // ordinary rename, while "one" → "one/inner" moved the dir inside itself
    // (the target doesn't exist yet, so the occupied-target test above sees
    // nothing).
    if (target.startsWith(e.path + "/")) return true;
    return dirAt.has(target) || fileAt.has(target);
  };

  const resolvePath = (e: Editing, value: string): string | null => {
    const raw = value.trim();
    if (!raw) return null;
    if (e.mode === "new-file") {
      // A typed extension wins — and ANY workspace extension counts, not just
      // this entry's: appending ".md" to "guide.markdown" minted a double
      // suffix, and ".arch" to a typed "package.archspace" a dead manifest.
      const hasExt = hasWorkspaceExt(raw) || (e.ext !== "" && raw.endsWith(e.ext));
      return resolveNewPath(e.dir, hasExt ? raw : `${raw}${e.ext}`);
    }
    if (e.mode === "new-dir") return resolveNewPath(e.dir, raw);
    if (e.mode === "rename-dir") {
      const parent = e.path.includes("/") ? e.path.slice(0, e.path.lastIndexOf("/")) : "";
      return resolveNewPath(parent, raw);
    }
    // rename-file: preserve the file's OWN extension. The tree lists every
    // workspace file (`.md` docs included), so force-appending ".arch" would
    // turn a renamed document into a compiling source. A bare name keeps the
    // original suffix — including NO suffix (LICENSE, Dockerfile, and dotfiles
    // like `.gitignore`, whose leading dot is not an extension).
    const dot = e.path.lastIndexOf(".");
    const ext = dot > e.path.lastIndexOf("/") + 1 ? e.path.slice(dot) : "";
    // "has a dot" is NOT "has an extension": dotted stems are everywhere in this
    // ecosystem (`arch.cloud.aws`), and treating one as its own suffix renamed a
    // source to an extensionless file that no longer compiles.
    const typedExt = hasWorkspaceExt(raw) || (ext !== "" && raw.endsWith(ext)) || ext === "";
    return resolveNewPath(bareDir(e.path), typedExt ? raw : `${raw}${ext}`);
  };

  /** True when the edit was applied; false = REFUSED, editor stays open. */
  const commitEditing = (value: string): boolean => {
    const e = editing;
    if (!e) return true;
    const target = resolveEdit(e, value);
    if (target === null) {
      // Keep the editor open on a name the workspace can't hold, carrying what
      // was typed (`draftRef`): closing it silently was indistinguishable from
      // the "+ did nothing" bug this whole area exists to fix.
      if (value.trim()) return false;
      setEditing(null);
      return true;
    }
    setEditing(null);
    draftRef.current = null;
    // A search filter that doesn't match the new name would hide it the instant
    // it is created — the file exists and is active, but the tree shows nothing:
    // indistinguishable from "the + did nothing", which is the symptom this
    // whole area exists to remove. Creating something is a deliberate move to
    // that thing, so the filter yields to it.
    if ((e.mode === "new-file" || e.mode === "new-dir") && query.trim() !== "") {
      // A FOLDER can never show under a query: while one is active the tree is
      // replaced by a flat list of matching FILES, and a pending dir holds no
      // files yet. So a new dir always clears it. A new file is tested against
      // the same predicate `filteredFiles` uses — the full path, not the
      // basename, so filtering to `payments/` and creating a file inside it
      // keeps the filter (and the user's place in a big tree).
      const needle = query.trim().toLowerCase();
      if (e.mode === "new-dir" || !(lead + target).toLowerCase().includes(needle)) setQuery("");
    }
    if (e.mode === "new-file") {
      // A manifest is born with its one required field: an empty
      // `package.archspace` is a parse error (the parser demands `package:` or
      // `name:`), so an unseeded one would land in the tree already broken. The
      // seed describes the dir the file LANDS in (a typed name may nest).
      const ownDir = target.includes("/") ? target.slice(0, target.lastIndexOf("/")) : "";
      const content = isManifestPath(target)
        ? manifestSeed(
            ownDir,
            hasAncestorManifest(
              ownDir,
              files.map((f) => f.path),
            ),
          )
        : undefined;
      source.create?.(lead + target, content);
    } else if (e.mode === "new-dir") {
      setPendingDirs((p) => new Set(p).add(target));
      expandDir(target);
    } else if (e.mode === "rename-file") {
      const next = lead + target;
      if (next !== e.path) source.rename?.(e.path, next);
    } else if (e.mode === "rename-dir") {
      renameDir(e.path, target);
    }
    return true;
  };

  /** `nextDir` is an already-resolved bare dir path (see `resolveEdit`). */
  const renameDir = (dir: string, nextDir: string): void => {
    if (nextDir === dir) return;
    for (const f of descendants(dir)) {
      const bare = f.path.replace(/^\/+/, "");
      source.rename?.(f.path, lead + nextDir + bare.slice(dir.length));
    }
    setPendingDirs((p) => {
      const n = new Set<string>();
      for (const d of p)
        n.add(d === dir ? nextDir : d.startsWith(dir + "/") ? nextDir + d.slice(dir.length) : d);
      return n;
    });
  };

  const deleteDir = (dir: string): void => {
    for (const f of descendants(dir)) source.delete?.(f.path);
    setPendingDirs((p) => new Set([...p].filter((x) => x !== dir && !x.startsWith(dir + "/"))));
  };

  // --- search --------------------------------------------------------------

  useEffect(() => {
    if (!hotkey) return;
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hotkey]);

  const q = query.trim().toLowerCase();
  // A file matches by path OR by any entity it declares (module / interface /
  // process), so searching "PlaceOrder" surfaces the file that declares it.
  const fileSearchText = useMemo(() => {
    const collect = (parts: string[], m: Module): void => {
      parts.push(m.name.toLowerCase());
      for (const i of m.interfaces) parts.push(i.name.toLowerCase());
      for (const c of m.children) collect(parts, c);
    };
    const map = new Map<string, string>();
    for (const f of files) {
      const parts: string[] = [];
      for (const m of modulesByFile.get(fileKey(f.path)) ?? []) collect(parts, m);
      for (const p of processesByFile.get(fileKey(f.path)) ?? []) parts.push(p.name.toLowerCase());
      map.set(f.path, parts.join("\n"));
    }
    return map;
  }, [files, modulesByFile, processesByFile]);
  const filteredFiles = useMemo(
    () =>
      q
        ? viewFiles.filter(
            (f) =>
              f.path.toLowerCase().includes(q) ||
              (fileSearchText.get(f.path) ?? "").includes(q),
          )
        : viewFiles,
    [viewFiles, q, fileSearchText],
  );

  // Viewer FilteredResults parity: a files-tab search ALSO lists matching
  // entities as flat rows with qualified-path context, so "PlaceOrder" finds
  // the interface itself, not just its file.
  interface EntityMatch {
    readonly key: string;
    readonly label: string;
    readonly icon: ReactNode;
    readonly hint?: string;
    readonly suffix?: string;
    readonly active?: boolean;
    readonly derived?: boolean;
    readonly faded?: boolean;
    readonly onClick: () => void;
    readonly onOpen: () => void;
  }
  const entityMatches = useMemo<EntityMatch[]>(() => {
    if (!q) return [];
    const out: EntityMatch[] = [];
    const walk = (m: Module, parentPath: string): void => {
      const qn = parentPath ? `${parentPath}.${m.name}` : m.name;
      if (m.name.toLowerCase().includes(q) || qn.toLowerCase().includes(q)) {
        out.push({
          // Stable-key form — a bare qualified path duplicates across spaces
          // (two spaces may each own `Api`), breaking React list keys (v0.11).
          key: `m:${moduleKey(m)}`,
          label: m.name,
          icon: <ModuleIcon module={m} typeName={m.typeName} />,
          hint: m.space ? (parentPath ? `${m.space}.${parentPath}` : m.space) : parentPath || undefined,
          suffix: m.typeName,
          active: sel.moduleId !== null && moduleKey(m) === sel.moduleId,
          derived: !!m.unnamed,
          onClick: () => onModule(m),
          onOpen: () => onModuleOpen(m),
        });
      }
      for (const i of m.interfaces) {
        const iq = `${qn}.${i.name}`;
        if (i.name.toLowerCase().includes(q) || iq.toLowerCase().includes(q)) {
          out.push({
            key: `i:${iq}`,
            label: interfaceDisplayName(i),
            icon: <InterfaceIcon />,
            hint: qn,
            suffix: i.typeName,
            faded: isAnonymousInterface(i),
            onClick: () => onInterface(m, i.name),
            onOpen: () => onInterfaceOpen(m, i.name),
          });
        }
      }
      for (const c of m.children) walk(c, qn);
    };
    for (const m of model?.modules ?? []) walk(m, "");
    for (const p of processes) {
      if (labelForProcess(p).toLowerCase().includes(q)) {
        out.push({
          key: `p:${processKey(p)}`,
          label: labelForProcess(p),
          icon: <ProcessIcon />,
          // D5: the space breadcrumb tells twin same-named processes apart.
          hint: [p.space, ...(p.ownerPath ?? [])].filter(Boolean).join(".") || undefined,
          suffix: "process",
          active: sel.processId !== null && processKey(p) === sel.processId,
          onClick: () => onProcess(p),
          onOpen: () => onProcessOpen(p),
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onModule/onInterface/onProcess are stable per-render closures over `source`
  }, [q, model, processes, sel.moduleId, sel.processId]);

  // --- context menu --------------------------------------------------------

  // Touch long-press bookkeeping for the synthesized context menu.
  const navLongPress = useRef<{ timer: number; x: number; y: number } | null>(null);
  // When the long-press fires, the finger lift that follows still
  // synthesizes a click (iOS) — swallow it, or it would instantly close
  // the menu AND select the pressed row.
  const navLongPressFiredAt = useRef(-Infinity);
  const cancelNavLongPress = (): void => {
    if (navLongPress.current !== null) {
      clearTimeout(navLongPress.current.timer);
      navLongPress.current = null;
    }
  };
  // Clear a pending long-press timer if the nav unmounts mid-press, so it can't
  // fire a synthesized contextmenu against a detached node.
  useEffect(() => cancelNavLongPress, []);

  const openContextMenu = (e: ReactMouseEvent, target: MenuTarget): void => {
    e.preventDefault();
    e.stopPropagation();
    // No actions for this target (host wired nothing) -> no empty menu.
    if (menuItems(target).length === 0) return;
    setMenu({ x: e.clientX, y: e.clientY, target });
  };
  const onModuleContextMenu = (m: Module, e: ReactMouseEvent): void =>
    openContextMenu(e, { kind: "module", id: moduleKey(m) });
  const onProcessContextMenu = (p: Process, e: ReactMouseEvent): void =>
    openContextMenu(e, { kind: "process", id: processKey(p) });

  const menuItems = (target: MenuTarget): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];
    if (target.kind === "file") {
      const p = target.file.path;
      items.push({ label: t("menu.open"), onClick: () => source.open(p) });
      if (canRename)
        items.push({ label: t("menu.rename"), onClick: () => {
            draftRef.current = null;
            setEditing({ mode: "rename-file", path: p });
          } });
      if (canDuplicate) items.push({ label: t("menu.duplicate"), onClick: () => source.duplicate?.(p) });
      items.push({ label: t("menu.copyPath"), onClick: () => void navigator.clipboard?.writeText(p) });
      if (target.file.status && canRevert)
        items.push({ label: t("menu.revert"), onClick: () => source.revert?.(p) });
      if (canDelete)
        items.push({ label: t("menu.delete"), danger: true, separator: true, onClick: () => source.delete?.(p) });
    } else if (target.kind === "dir") {
      const d = target.path;
      if (canCreate) {
        items.push({ label: t("menu.newFile"), onClick: () => startNewFile(d) });
        items.push({ label: t("menu.newFolder"), onClick: () => startNewDir(d) });
      }
      if (canRename)
        items.push({ label: t("menu.rename"), onClick: () => {
            draftRef.current = null;
            setEditing({ mode: "rename-dir", path: d });
          } });
      items.push({ label: t("menu.copyPath"), onClick: () => void navigator.clipboard?.writeText(d) });
      if (canDelete)
        items.push({ label: t("menu.delete"), danger: true, separator: true, onClick: () => deleteDir(d) });
    } else if (target.kind === "module") {
      // "Show on board": fly the board to the module. Prefer the explicit
      // host action (which also flips a phone to the board surface); fall
      // back to the double-click open.
      const show = source.showModuleOnBoard ?? source.openModule;
      if (show) items.push({ label: t("menu.showOnBoard"), onClick: () => show(target.id) });
    } else if (target.kind === "process") {
      // "Show on board": activate the process highlight on the board.
      const show = source.showProcessOnBoard ?? source.selectProcess;
      if (show) items.push({ label: t("menu.showOnBoard"), onClick: () => show(target.id) });
    } else {
      if (canCreate) {
        items.push({ label: t("menu.newFile"), onClick: () => startNewFile("") });
        items.push({ label: t("menu.newFolder"), onClick: () => startNewDir("") });
      }
    }
    return items;
  };

  // --- inline edit input ---------------------------------------------------

  const renderInput = (initial: string): ReactElement => {
    const born = editing?.mode === "new-file" ? editing.initial || initial : initial;
    return (
      <InlineNameInput
      // Identity = everything the component snapshots on mount. Without `ext`
      // and `initial`, picking a second file type reused the first editor
      // (stale prefill, spent settle guard).
      key={
        editing
          ? editing.mode === "new-file"
            ? `new-file:${editing.dir}:${editing.ext}:${editing.initial}`
            : `${editing.mode}:${"dir" in editing ? editing.dir : editing.path}`
          : "none"
      }
      initial={draftRef.current ?? born}
      pristine={born}
      // `draftRef` is the parent-held record of typing; it is set on every
      // keystroke and cleared when an edit starts or ends, so it survives the
      // remount and answers this exactly.
      wasEdited={() => draftRef.current !== null}
      ext={editing?.mode === "new-file" ? editing.ext : null}
      label={t("nameAria")}
      validate={(v) => (editing ? resolveEdit(editing, v) !== null : true)}
      onDraft={(v) => {
        draftRef.current = v;
      }}
      onCommit={commitEditing}
      onCancel={() => {
        draftRef.current = null;
        setEditing(null);
      }}
      />
    );
  };

  // --- file / dir rows -----------------------------------------------------

  const renderFile = (file: NavFile, depth: number): ReactNode => {
    const path = file.path;
    const name = decodeLabel(basename(path));
    const active = path === sel.activeFile;
    const renaming = editing?.mode === "rename-file" && editing.path === path;
    const mods = modulesByFile.get(fileKey(path)) ?? [];
    const procs = processesByFile.get(fileKey(path)) ?? [];
    const fileViews = viewsByFile.get(fileKey(path)) ?? [];
    const stubs = externIndex.stubsByFile.get(fileKey(path)) ?? [];
    const hasEntities =
      mods.length > 0 || procs.length > 0 || fileViews.length > 0 || stubs.length > 0;
    const isOpen = hasEntities && !collapsedFiles.has(path);
    const base = depth + 1; // entities sit one level under their file
    return (
      <div
        key={path}
        draggable={canMove && !renaming}
        onDragStart={() => canMove && setDragSrc(path)}
        onDragEnd={() => {
          setDragSrc(null);
          setDropDir(null);
        }}
      >
        <div
          style={{ paddingLeft: 4 + depth * 12 }}
          onContextMenu={(e) => openContextMenu(e, { kind: "file", file })}
          className={cx(
            "group flex items-center gap-1 pr-1 text-[13px] pointer-coarse:gap-2 pointer-coarse:text-[15px]",
            active ? "bg-selection" : "hover:bg-hover",
            dragSrc === path && "opacity-50",
          )}
        >
          <button
            type="button"
            onClick={() => hasEntities && toggleFile(path)}
            aria-label={isOpen ? t("collapse") : t("expand")}
            aria-expanded={hasEntities ? isOpen : undefined}
            className={cx(
              "flex w-3 shrink-0 justify-center text-slate-500 pointer-coarse:w-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950",
              !hasEntities && "invisible",
            )}
          >
            <Chevron open={isOpen} />
          </button>
          <FileIcon active={active} path={path} />
          {renaming ? (
            // The RAW basename, not the display-decoded `name`: a rename edits
            // the STORAGE KEY. Prefilling the decoded form made pressing Enter
            // on an untouched `services%2Fapi.arch` resolve to
            // `services/api.arch` — a different path, so the nav silently
            // RELOCATED the file into a real directory.
            <div className="flex-1 py-0.5">{renderInput(basename(path))}</div>
          ) : (
            <>
              <Tooltip content={path}>
                <button
                  type="button"
                  aria-current={active ? "true" : undefined}
                  onClick={() => source.open(path)}
                  onDoubleClick={() => (source.openInNewTab ?? source.open)(path)}
                  className={cx(
                    "flex-1 truncate py-1 text-left pointer-coarse:py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950",
                    active ? "text-white" : "text-slate-200",
                  )}
                >
                  {name}
                </button>
              </Tooltip>
              <StatusBadge status={file.status} />
              {file.status && canRevert && (
                <Tooltip content={t("revertFileTitle", { name })}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => source.revert?.(path)}
                    aria-label={t("revertFileAria", { name })}
                    className="shrink-0 opacity-0 hover:text-arch-300 group-hover:opacity-100"
                  >
                    ↺
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip content={t("deleteFileTitle", { name })}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => source.delete?.(path)}
                    aria-label={t("deleteFileAria", { name })}
                    className="shrink-0 opacity-0 hover:text-red-400 group-hover:opacity-100"
                  >
                    ×
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </div>
        {isOpen && (
          <div className="pb-1">
            {mods.map((m) => (
              <ModuleNode
                key={moduleKey(m)}
                module={m}
                path={m.name}
                depth={base}
                selectedId={sel.moduleId}
                hoverStore={hoverStore}
                expanded={expandedModules}
                onToggleModule={toggleModule}
                externByPath={externIndex.externByPath}
                registerRow={registerRow}
                onJumpToStub={jumpToStub}
                onModule={onModule}
                onModuleOpen={onModuleOpen}
                onModuleContextMenu={onModuleContextMenu}
                onInterface={onInterface}
                onInterfaceOpen={onInterfaceOpen}
              />
            ))}
            {procs.map((p) => (
              <EntityRow
                key={processKey(p)}
                depth={base}
                icon={<ProcessIcon />}
                label={displayLabel(p.name)}
                active={sel.processId !== null && processKey(p) === sel.processId}
                onClick={() => onProcess(p)}
                onDoubleClick={() => onProcessOpen(p)}
                onContextMenu={(e) => onProcessContextMenu(p, e)}
              />
            ))}
            {fileViews.map((v) => (
              <EntityRow
                key={`view:${v.id ?? v.name}`}
                depth={base}
                icon={<ViewIcon />}
                label={displayLabel(bareViewLabel(v.name))}
                active={selectedViewName === v.name}
                onClick={() => (source.selectView ?? source.openView)?.(v.name)}
                onDoubleClick={() => source.openView?.(v.name)}
              />
            ))}
            {stubs.map((s) => (
              <EntityRow
                key={`stub:${s.path}`}
                depth={base}
                icon={<ModuleIcon module={s.module} typeName={s.module.typeName} />}
                label={`${s.parentPathLabel}.${s.module.name}`}
                suffix={s.module.typeName}
                faded
                active={sel.moduleId !== null && moduleKey(s.module) === sel.moduleId}
                hoverStore={hoverStore}
                hoverId={moduleKey(s.module)}
                innerRef={registerRow(`stub:${s.path}`)}
                title={t("stubTitle", { parent: s.parentPathLabel })}
                action={{
                  icon: "↰",
                  title: t("stubJumpToReal", {
                    target: `${s.parentPathLabel}.${s.module.name}`,
                    parent: s.parentPathLabel,
                  }),
                  onClick: () => jumpToReal(s),
                }}
                onClick={() => onModule(s.module)}
                onDoubleClick={() => onModuleOpen(s.module)}
                onContextMenu={(e) => onModuleContextMenu(s.module, e)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDir = (node: DirNode, depth: number): ReactNode => {
    const open = expandedDirs.has(node.path);
    const isPackage = packageDirs?.has(node.path) ?? false;
    const isSpace = !isPackage && (spaceDirs?.has(node.path) ?? false);
    const childDirs = [...node.dirs.values()].sort((a, b) => a.name.localeCompare(b.name));
    const childFiles = [...node.files].sort((a, b) => a.name.localeCompare(b.name));
    const newHere =
      editing && (editing.mode === "new-file" || editing.mode === "new-dir") && editing.dir === node.path;
    const renamingDir = editing?.mode === "rename-dir" && editing.path === node.path;
    if (renamingDir) {
      return (
        <div key={`d:${node.path}`} style={{ paddingLeft: 4 + depth * 12 }} className="py-0.5 pr-2">
          {renderInput(node.name)}
        </div>
      );
    }
    return (
      <div key={`d:${node.path}`}>
        <div
          style={{ paddingLeft: 4 + depth * 12 }}
          onClick={() => toggleDir(node.path)}
          onContextMenu={(e) => openContextMenu(e, { kind: "dir", path: node.path })}
          onDragOver={canMove ? (e) => { e.preventDefault(); setDropDir(node.path); } : undefined}
          onDragLeave={() => setDropDir((d) => (d === node.path ? null : d))}
          onDrop={
            canMove
              ? (e) => {
                  e.preventDefault();
                  if (dragSrc && bareDir(dragSrc) !== node.path) source.move?.(dragSrc, node.path);
                  setDragSrc(null);
                  setDropDir(null);
                }
              : undefined
          }
          role="button"
          aria-expanded={open}
          className={cx(
            "flex cursor-pointer items-center gap-1 truncate py-1 pr-2 text-left text-[13px] text-slate-300 hover:bg-hover pointer-coarse:gap-2 pointer-coarse:py-3 pointer-coarse:text-[15px] pointer-coarse:rounded-lg pointer-coarse:active:bg-white/10",
            dropDir === node.path && "bg-arch-600/20 ring-1 ring-inset ring-arch-500",
            (isPackage || isSpace) && "border-l-2 border-arch-500/40",
          )}
        >
          <span className="flex w-3 shrink-0 justify-center text-slate-500 pointer-coarse:w-7">
            <Chevron open={open} />
          </span>
          {open ? (
            <FolderOpen className="h-[13px] w-[13px] shrink-0 text-slate-300" strokeWidth={1.8} aria-hidden="true" />
          ) : (
            <Folder className="h-[13px] w-[13px] shrink-0 text-slate-300" strokeWidth={1.8} aria-hidden="true" />
          )}
          <span className="truncate font-medium">{node.name}</span>
          {isPackage ? (
            <span title={t("packageRootTitle")} className="ml-1 shrink-0 rounded bg-arch-600/30 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-arch-300">pkg</span>
          ) : isSpace ? (
            <span title={t("spaceRootTitle")} className="ml-1 shrink-0 rounded bg-slate-700/(--tint-dim) px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-300">space</span>
          ) : null}
        </div>
        {newHere && open && (
          <div style={{ paddingLeft: 4 + (depth + 1) * 12 }} className="py-0.5 pr-2">
            {renderInput("")}
          </div>
        )}
        {open && (
          <>
            {childDirs.map((d) => renderDir(d, depth + 1))}
            {childFiles.map((f) => renderFile(f.file, depth + 1))}
          </>
        )}
      </div>
    );
  };

  const rootDirs = [...tree.dirs.values()].sort((a, b) => a.name.localeCompare(b.name));
  const rootFiles = [...tree.files].sort((a, b) => a.name.localeCompare(b.name));
  const newAtRoot =
    editing && (editing.mode === "new-file" || editing.mode === "new-dir") && editing.dir === "";

  // Flat (search) results: matching files first, then matching entities with
  // qualified-path context (viewer FilteredResults parity).
  const renderFlatFiles = (): ReactNode => (
    <>
      {/* An open new-file/new-dir editor survives the switch to flat results:
          the tree it lived in isn't rendered while a query is active, and
          unmounting it threw away what the user had typed. */}
      {(editing?.mode === "new-file" || editing?.mode === "new-dir") && (
        <div style={{ paddingLeft: 4 }} className="py-0.5 pr-2">
          {renderInput("")}
        </div>
      )}
      {filteredFiles.length === 0 && entityMatches.length === 0 ? (
        <div className="px-3 py-2 text-slate-600">{t("nothingMatches", { query })}</div>
      ) : (
        <>
        {[...filteredFiles]
          .sort((a, b) => a.path.localeCompare(b.path))
          .map((f) => renderFile(f, 0))}
        {entityMatches.length > 0 && (
          <>
            <div className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              {t("entities")}
            </div>
            {entityMatches.map((e) => (
              <EntityRow
                key={e.key}
                depth={0}
                icon={e.icon}
                label={e.label}
                hint={e.hint}
                suffix={e.suffix}
                active={e.active}
                derived={e.derived}
                faded={e.faded}
                onClick={e.onClick}
                onDoubleClick={e.onOpen}
              />
            ))}
          </>
        )}
        </>
      )}
    </>
  );

  // The view/mode tabs (+ a Problems tab) — `big` lays them out as a
  // full-width finger-height bottom bar on a narrow viewport. Assumes !diff.
  //
  // Every rung of the rail (host tabs / the built-in fallback / Problems) is
  // described as ONE list of `RailTab`s and rendered by ONE map over the shared
  // `TabHandle`. Problems is not special-cased: it is a rail tab whose
  // complications happen to be a severity-tinted icon + a count badge.
  const renderTabList = (big: boolean): ReactNode => {
    interface RailTab {
      readonly id: string;
      readonly label: string;
      readonly icon: ReactNode;
      readonly active: boolean;
      readonly onSelect: () => void;
      readonly tooltip: ReactNode;
      readonly disabled?: boolean;
      readonly badge?: TabHandleBadge | null;
      readonly overlay?: ReactNode;
    }
    const railTabs: RailTab[] = [];
    if (tabs) {
      for (const tab of tabs.items) {
        railTabs.push({
          id: tab.id,
          label: tab.label,
          icon: tab.icon,
          active: !diagOpen && tabs.active === tab.id,
          disabled: tab.disabled,
          badge: tab.badge,
          overlay: tab.overlay,
          tooltip: tab.title ?? tab.label,
          onSelect: () => {
            setDiagOpen(false);
            tabs.onSelect(tab.id);
          },
        });
      }
    } else if (controlledView === undefined) {
      for (const id of ["files", "types", "processes"] as const) {
        const tabLabel = t(`tab.${id}`);
        railTabs.push({
          id,
          label: tabLabel,
          icon: <TabIcon id={id} />,
          active: !diagOpen && view === id,
          tooltip: tabLabel,
          onSelect: () => {
            setDiagOpen(false);
            setView(id);
          },
        });
      }
    }
    if (diagSummary) {
      railTabs.push({
        id: "diagnostics",
        label: diagSummary.total
          ? t("problemsCount", { count: diagSummary.total })
          : t("problems"),
        icon: <DiagTabIcon worst={diagSummary.worst} />,
        active: diagOpen,
        onSelect: () => setDiagOpen(true),
        badge: { count: diagSummary.total, tone: DIAG_BADGE_TONE[diagSummary.worst] },
        tooltip: diagSummary.total
          ? t("problemsSummary", {
              errors: diagSummary.error,
              warnings: diagSummary.warning,
              todos: diagSummary.todo,
              info: diagSummary.info,
            })
          : t("noProblems"),
      });
    }
    return (
      <TabHandleStrip
        label={t("navigator")}
        className={cx(
          "flex",
          big
            ? "w-full items-stretch gap-1 rounded-2xl border border-white/10 bg-white/[0.04] p-1"
            : "items-center gap-0.5",
        )}
      >
        {railTabs.map((tab) => (
          <Tooltip key={tab.id} content={tab.tooltip}>
            <TabHandle
              label={tab.label}
              icon={tab.icon}
              active={tab.active}
              disabled={tab.disabled}
              badge={tab.badge}
              overlay={tab.overlay}
              size={big ? "touch" : "sm"}
              onClick={tab.onSelect}
            />
          </Tooltip>
        ))}
      </TabHandleStrip>
    );
  };

  const renderDiagnostics = (): ReactNode => {
    if (!diagnostics || diagnostics.length === 0) {
      return <EmptyState title={t("noProblemsDetected")} />;
    }
    return diagnostics.map((d, i) => {
      const InlineIcon =
        d.severity === "todo"
          ? ListTodo
          : d.severity === "info" || d.severity === "hint"
            ? Info
            : TriangleAlert;
      return (
        <Tooltip
          key={`${d.file ?? ""}:${d.severity}:${d.message}:${i}`}
          content={diagnosticText(d, locale)}
        >
          <button
            type="button"
            onClick={() => d.file && source.open(d.file)}
            className="flex w-full items-start gap-2 px-2 py-1.5 text-left text-[13px] pointer-coarse:py-3 pointer-coarse:text-[15px] pointer-coarse:rounded-lg pointer-coarse:active:bg-white/10 hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            <InlineIcon
              className={cx("mt-0.5 h-3.5 w-3.5 shrink-0", DIAG_ROW_COLOR[d.severity])}
              strokeWidth={1.9}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-slate-200">{diagnosticText(d, locale)}</span>
              {d.file && (
                <span className="block truncate text-[11px] text-slate-500">{basename(d.file)}</span>
              )}
            </span>
          </button>
        </Tooltip>
      );
    });
  };

  const hasTabs = !diff && (!!tabs || controlledView === undefined || !!diagSummary);
  const hasActions =
    readonly ||
    (canRevert && anyDirty && !!source.revertAll) ||
    (canCreate && view === "files" && !diff) ||
    !!headerExtra;

  return (
    <NodeColoringProvider value={coloring ?? null}>
    <div
      // select-none + no touch-callout: a long-press opens the context
      // menu — without these, iOS/Android ALSO start selecting the row text
      // (and on iOS the copy callout pops over the sheet).
      className={cx(
        "flex h-full min-h-0 flex-col select-none [-webkit-touch-callout:none] bg-panel-secondary text-xs text-slate-200",
        className,
      )}
      data-testid="workspace-nav"
      aria-label={t("workspaceNavigator")}
    >
      {(diff || (hasTabs && !narrow) || hasActions) && (
        <div className="flex shrink-0 items-center gap-1 px-1.5 py-1">
          {diff ? (
            <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-300">
              {t("changes")}
            </span>
          ) : !narrow ? (
            renderTabList(false)
          ) : null}
          <div className="ml-auto flex items-center gap-1">
            {readonly && !diff && (
              <span
                className="rounded bg-slate-800 px-1 text-[10px] uppercase tracking-wide text-slate-400"
                title={t("readOnlyTitle")}
              >
                {t("readOnly")}
              </span>
            )}
            {canRevert && anyDirty && source.revertAll && (
              <Tooltip content={t("revertAllTitle")}>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => source.revertAll?.()}
                  className="shrink-0"
                >
                  {t("revertAll")}
                </Button>
              </Tooltip>
            )}
            {canCreate && view === "files" && !diff && !activeSectionPanel && (
              // Every item here mints an inline name input and hands it focus;
              // the menu's own focus-return fights that (see InlineNameInput).
              <Menu placement="bottom-end">
                <MenuButton
                  aria-label={t("newFile")}
                  title={t("newFile")}
                  className="rounded p-1 text-slate-400 hover:bg-hover hover:text-white focus-visible:outline-none"
                >
                  <Plus size={14} strokeWidth={2} aria-hidden />
                </MenuButton>
                <MenuItems>
                  <MenuItem
                    onClick={() => {
                      const dir = sel.activeFile ? bareDir(sel.activeFile) : "";
                      startNewFile(dir, ARCH_EXT);
                    }}
                  >
                    <Braces size={16} strokeWidth={1.75} aria-hidden className="shrink-0 opacity-60" />
                    <span className="flex-1">{t("newFileArch")}</span>
                    <span className="text-[11px] text-slate-500">{ARCH_EXT}</span>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const dir = sel.activeFile ? bareDir(sel.activeFile) : "";
                      // Prefilled "package": that stem is the ONLY name the
                      // toolchain reads as a manifest.
                      startNewFile(dir, ARCHSPACE_EXT, "package");
                    }}
                  >
                    <Package size={16} strokeWidth={1.75} aria-hidden className="shrink-0 opacity-60" />
                    <span className="flex-1">{t("newFileManifest")}</span>
                    <span className="text-[11px] text-slate-500">{ARCHSPACE_EXT}</span>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const dir = sel.activeFile ? bareDir(sel.activeFile) : "";
                      startNewFile(dir, MD_EXT);
                    }}
                  >
                    <FileText size={16} strokeWidth={1.75} aria-hidden className="shrink-0 opacity-60" />
                    <span className="flex-1">{t("newFileDoc")}</span>
                    <span className="text-[11px] text-slate-500">{MD_EXT}</span>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const dir = sel.activeFile ? bareDir(sel.activeFile) : "";
                      // No extension appended — whatever the user types IS the
                      // name (archlang.json, a widget script, LICENSE).
                      startNewFile(dir, "");
                    }}
                  >
                    <File size={16} strokeWidth={1.75} aria-hidden className="shrink-0 opacity-60" />
                    <span className="flex-1">{t("newFileOther")}</span>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const dir = sel.activeFile ? bareDir(sel.activeFile) : "";
                      startNewDir(dir);
                    }}
                  >
                    <FolderPlus size={16} strokeWidth={1.75} aria-hidden className="shrink-0 opacity-60" />
                    <span className="flex-1">{t("newFolder")}</span>
                  </MenuItem>
                </MenuItems>
              </Menu>
            )}
            {headerExtra}
          </div>
        </div>
      )}

      {!diagOpen && (
      <div className="shrink-0 px-1.5 py-1 pointer-coarse:px-3 pointer-coarse:py-2">
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-coarse:left-3.5"
          >
            ⌕
          </span>
          <input
            ref={(el) => {
              searchRef.current = el;
              if (searchInputRef) searchInputRef.current = el;
            }}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setQuery("");
                e.currentTarget.blur();
              }
            }}
            placeholder={cx(diff ? t("searchChanges") : t("search"), hotkey && t("hotkeyHint"))}
            aria-label={t("searchAria")}
            spellCheck={false}
            autoComplete="off"
            className="w-full rounded border border-input-border bg-input py-1 pl-6 pr-6 text-slate-200 placeholder:text-slate-500 outline-none focus:border-accent [&::-webkit-search-cancel-button]:hidden pointer-coarse:border-transparent pointer-coarse:rounded-full pointer-coarse:bg-white/[0.07] pointer-coarse:py-2.5 pointer-coarse:pl-9 pointer-coarse:pr-9 pointer-coarse:text-[15px] pointer-coarse:focus:bg-white/10"
          />
          {query && (
            <Tooltip content={t("clearSearch")}>
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={t("clearSearch")}
                onClick={() => setQuery("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 pointer-coarse:right-2.5 pointer-coarse:text-lg"
              >
                ×
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      )}

      <div
        className="min-h-0 flex-1 overflow-y-auto py-0.5 pointer-coarse:px-1.5"
        onContextMenu={(e) => {
          if (!diff && !diagOpen && view === "files" && e.target === e.currentTarget)
            openContextMenu(e, { kind: "root", path: "" });
        }}
        // Touch long-press -> context menu. iOS Safari never fires
        // `contextmenu` for touch, so a still half-second press synthesizes
        // one at the pressed row; Android fires the native event too -- both
        // routes land in the same openContextMenu, harmlessly.
        onPointerDown={(e) => {
          if (e.pointerType !== "touch") return;
          cancelNavLongPress();
          const target = e.target as HTMLElement;
          const { clientX, clientY } = e;
          navLongPress.current = {
            x: clientX,
            y: clientY,
            timer: window.setTimeout(() => {
              navLongPress.current = null;
              navLongPressFiredAt.current = performance.now();
              target.dispatchEvent(
                new MouseEvent("contextmenu", {
                  bubbles: true,
                  cancelable: true,
                  clientX,
                  clientY,
                }),
              );
            }, 500),
          };
        }}
        onPointerMove={(e) => {
          const lp = navLongPress.current;
          // A finger that travels is a scroll, not a press.
          if (lp && Math.hypot(e.clientX - lp.x, e.clientY - lp.y) > 10) cancelNavLongPress();
        }}
        onPointerUp={cancelNavLongPress}
        onPointerCancel={cancelNavLongPress}
        // Native touch long-press contextmenu (Chrome/Android, devtools
        // mobile emulation) is suppressed — the synthesized 500ms path is
        // the single source, so timing/behavior match iOS exactly. Desktop
        // right-click (pointerType mouse) and our synthesized event
        // (isTrusted=false) pass through untouched.
        onContextMenuCapture={(e) => {
          const pt = (e.nativeEvent as Partial<PointerEvent>).pointerType;
          const touchNative =
            e.nativeEvent.isTrusted &&
            (pt === "touch" || (pt === undefined && navLongPress.current !== null));
          if (touchNative) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onClickCapture={(e) => {
          if (performance.now() - navLongPressFiredAt.current < 700) {
            e.preventDefault();
            e.stopPropagation();
            navLongPressFiredAt.current = -Infinity;
          }
        }}
      >
        {activeSectionPanel ? (
          activeSectionPanel
        ) : diagOpen ? (
          renderDiagnostics()
        ) : (
          <>
        {diff && (
          <ChangesView
            changeSet={diff}
            selected={source.diffSelection?.() ?? null}
            onFocus={(ref) => source.focus?.(ref)}
            query={q}
          />
        )}

        {/* Documents view: list markdown docs by display title (frontmatter
            `title`, else filename). Falls back to the `.md` file tree only when
            the source can't enumerate documents. */}
        {!diff && view === "documents" && source.documents && (
          <DocumentsView source={source} q={q} query={query} />
        )}

        {/* Views tab: v0.10 views, each opening on its own tab. */}
        {!diff && view === "views" && <ViewsView source={source} q={q} query={query} />}

        {!diff && (view === "files" || (view === "documents" && !source.documents)) &&
          (q ? (
            renderFlatFiles()
          ) : (
            <>
              {rootDirs.map((d) => renderDir(d, 0))}
              {rootFiles.map((f) => renderFile(f.file, 0))}
              {newAtRoot && (
                <div style={{ paddingLeft: 4 }} className="py-0.5 pr-2">
                  {renderInput("")}
                </div>
              )}
              {viewFiles.length === 0 && pendingDirs.size === 0 && (
                <EmptyState
                  title={
                    isDocs ? t("noDocuments") : canCreate ? t("noFilesCreatable") : t("noFiles")
                  }
                  hint={isDocs ? t("noDocumentsHint") : undefined}
                />
              )}
            </>
          ))}

        {!diff && view === "types" &&
          (q ? (
            <EntityList
              items={augmentedTypes.filter((ty) => ty.name.toLowerCase().includes(q))}
              empty={t("noTypesMatch", { query })}
              keyOf={(ty) => typeSelectionKey(ty)}
              render={(ty: TypeDef) => {
                const bk = baseTypeByName.get(ty.name);
                // Selection travels as the space-qualified key so two spaces'
                // same-named types stay distinct picks (r56).
                const key = typeSelectionKey(ty);
                return (
                  <EntityRow
                    depth={1}
                    icon={bk ? <TypeIcon type={ty} baseKind={bk} iconName={typeIconByName.get(ty.name)} /> : "{}"}
                    label={displayLabel(ty.name)}
                    suffix={ty.library}
                    active={sel.typeName === key}
                    onClick={() => onType(key)}
                    onDoubleClick={() => onTypeOpen(key)}
                  />
                );
              }}
            />
          ) : typeGroups.length === 0 ? (
            <EmptyState title={t("noTypesDeclared")} hint={t("noTypesHint")} />
          ) : (
            typeGroups.map(([lib, nodes]) => {
              const key = `types:${lib}`;
              const open = !collapsedGroups.has(key);
              return (
                <div key={lib}>
                  <EntityRow
                    depth={0}
                    label={lib}
                    strong
                    suffix={String(countTypeNodes(nodes))}
                    expand={{ open, onToggle: () => toggleGroup(key) }}
                    onClick={() => toggleGroup(key)}
                  />
                  {open && (
                    <TypeNodeRows
                      nodes={nodes}
                      depth={1}
                      selected={sel.typeName}
                      baseTypeByName={baseTypeByName}
                      iconByName={typeIconByName}
                      collapsed={collapsedGroups}
                      onToggle={toggleGroup}
                      onSelect={onType}
                      onOpen={onTypeOpen}
                    />
                  )}
                </div>
              );
            })
          ))}

        {!diff && view === "processes" &&
          (q ? (
            // Search: flat, owner-qualified labels (no tree).
            <EntityList
              items={processes.filter((p) => labelForProcess(p).toLowerCase().includes(q))}
              empty={t("noProcessesMatch", { query })}
              keyOf={(p) => processKey(p)}
              render={(p: Process) => (
                <EntityRow
                  depth={0}
                  icon={<ProcessIcon />}
                  expand={null}
                  label={labelForProcess(p)}
                  suffix={p.sourceFile ? basename(p.sourceFile) : undefined}
                  active={sel.processId !== null && processKey(p) === sel.processId}
                  onClick={() => onProcess(p)}
                  onDoubleClick={() => onProcessOpen(p)}
                  onContextMenu={(e) => onProcessContextMenu(p, e)}
                />
              )}
            />
          ) : !anyProcesses ? (
            <EmptyState title={t("noProcessesDeclared")} hint={t("noProcessesHint")} />
          ) : (
            processGroups.map((group) => {
              const open = !collapsedProcFiles.has(group.file);
              const label = group.file === UNKNOWN_FILE ? t("unknownFile") : basename(group.file);
              return (
                <div key={group.file}>
                  <EntityRow
                    depth={0}
                    icon={<Workflow className="h-[13px] w-[13px] shrink-0 text-slate-300 pointer-coarse:h-4 pointer-coarse:w-4" strokeWidth={1.8} aria-hidden="true" />}
                    label={label}
                    strong
                    title={group.file}
                    expand={{ open, onToggle: () => toggleProcFile(group.file) }}
                    onClick={() => toggleProcFile(group.file)}
                  />
                  {open && (
                    <>
                      <ProcessTreeRows
                        nodes={group.nodes}
                        depth={1}
                        collapsed={collapsedProcModules}
                        selectedModuleId={sel.moduleId}
                        hoverStore={hoverStore}
                        activeProcessId={sel.processId}
                        onToggle={toggleProcModule}
                        onModule={onModule}
                        onModuleOpen={onModuleOpen}
                        onProcess={onProcess}
                        onProcessOpen={onProcessOpen}
                        onModuleContextMenu={onModuleContextMenu}
                        onProcessContextMenu={onProcessContextMenu}
                      />
                      {group.roots.map((p) => (
                        <EntityRow
                          key={processKey(p)}
                          depth={1}
                          icon={<ProcessIcon />}
                          expand={null}
                          label={displayLabel(p.name)}
                          active={sel.processId !== null && processKey(p) === sel.processId}
                          onClick={() => onProcess(p)}
                          onDoubleClick={() => onProcessOpen(p)}
                          onContextMenu={(e) => onProcessContextMenu(p, e)}
                        />
                      ))}
                    </>
                  )}
                </div>
              );
            })
          ))}
          </>
        )}
      </div>

      {/* Narrow viewport: tabs ride a finger-height bottom strip — a
          floating segmented pill, no hard separator. */}
      {narrow && hasTabs && (
        <div className="shrink-0 px-2 pt-1.5 pb-[calc(0.375rem+env(safe-area-inset-bottom))]">
          {renderTabList(true)}
        </div>
      )}

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menuItems(menu.target)}
          onClose={() => setMenu(null)}
          sheet={narrow}
        />
      )}
    </div>
    </NodeColoringProvider>
  );
}

/** Rotating expand chevron — same affordance as Studio's FileExplorer. */
function Chevron({ open }: { open: boolean }): ReactElement {
  return (
    <ChevronRight
      className={cx("h-3 w-3 transition-transform pointer-coarse:h-4 pointer-coarse:w-4", open && "rotate-90")}
      strokeWidth={2.2}
      aria-hidden="true"
    />
  );
}

function FileIcon({ active, path }: { active?: boolean; path?: string }): ReactElement {
  // Markdown = document; everything else (.arch) = workflow.
  const Icon = path && isMarkdownPath(path) ? FileText : Workflow;
  return (
    <Icon
      className={cx("h-3.5 w-3.5 shrink-0", active ? "text-arch-300" : "text-slate-300")}
      strokeWidth={1.8}
      aria-hidden="true"
    />
  );
}

function StatusBadge({ status }: { status?: NavFile["status"] }): ReactElement | null {
  const { t } = useTranslation("nav");
  if (!status) return null;
  if (status === "added")
    return <span title={t("status.added")} aria-label={t("status.addedAria")} className="shrink-0 text-[0.6rem] font-bold text-emerald-400">A</span>;
  if (status === "removed")
    return <span title={t("status.removed")} aria-label={t("status.removedAria")} className="shrink-0 text-[0.6rem] font-bold text-red-400">D</span>;
  if (status === "renamed")
    return <span title={t("status.renamed")} aria-label={t("status.renamedAria")} className="shrink-0 text-[0.6rem] font-bold text-sky-400">R</span>;
  return <span title={t("status.modified")} aria-label={t("status.modifiedAria")} className="shrink-0 text-[0.6rem] font-bold text-amber-400">M</span>;
}

// Tab-strip icons (lucide): folder / braces / workflow / document.
function TabIcon({ id }: { id: NavView }): ReactElement {
  const props = { className: "h-5 w-5", strokeWidth: 1.8, "aria-hidden": true } as const;
  switch (id) {
    case "files":
      return <Folder {...props} />;
    case "types":
      return <Braces {...props} />;
    case "processes":
      return <ProcessGlyph className={props.className} />;
    case "documents":
      return <FileText {...props} />;
    case "views":
      return <Eye {...props} />;
  }
}

function EntityList<T>(props: {
  items: readonly T[];
  empty: string;
  keyOf: (t: T) => string;
  render: (t: T) => ReactNode;
}): ReactElement {
  if (props.items.length === 0) {
    return <div className="px-3 py-2 text-slate-600">{props.empty}</div>;
  }
  return (
    <>
      {props.items.map((it) => (
        <div key={props.keyOf(it)}>{props.render(it)}</div>
      ))}
    </>
  );
}

/** Documents view: every `.md` document as a single row labelled by its display
 *  title (frontmatter `title`, else filename), with its space shown faintly.
 *  A single click opens it in the center pane (the source switches to docs
 *  mode); the path is the tooltip. */
function DocumentsView({
  source,
  q,
  query,
}: {
  source: NavSource;
  q: string;
  query: string;
}): ReactElement {
  const { t } = useTranslation("nav");
  const docs = source.documents?.() ?? [];
  if (docs.length === 0) {
    return <EmptyState title={t("noDocuments")} hint={t("noDocumentsHint")} />;
  }
  const selected = source.selectedDocName?.() ?? null;
  const shown = q
    ? docs.filter(
        (d) =>
          d.name.toLowerCase().includes(q) || d.path.toLowerCase().includes(q),
      )
    : docs;
  return (
    <EntityList
      items={shown}
      empty={t("noDocumentsMatch", { query })}
      keyOf={(d) => `${d.path}::${d.name}`}
      render={(d) => (
        <EntityRow
          depth={1}
          icon={<FileText className="h-3.5 w-3.5 text-slate-400" />}
          label={displayLabel(d.name)}
          suffix={d.space}
          title={d.path}
          active={selected === d.name}
          onClick={() => source.selectDocument?.(d.name)}
        />
      )}
    />
  );
}

/** A view's iconic symbol — the eye, the universal view glyph (matches the
 *  view tab + mode-switcher), tinted like a sibling entity class to processes. */
function ViewIcon(): ReactElement {
  return (
    <Eye
      className="h-3.5 w-3.5 shrink-0 text-slate-200 pointer-coarse:h-[18px] pointer-coarse:w-[18px]"
      strokeWidth={1.8}
      aria-hidden="true"
    />
  );
}

/** Views tab: every v0.10 view as a row (icon = the eye view glyph), the source
 *  file shown faintly. A click opens the view on its own tab. */
function ViewsView({
  source,
  q,
  query,
}: {
  source: NavSource;
  q: string;
  query: string;
}): ReactElement {
  const { t } = useTranslation("nav");
  const views = source.views?.() ?? [];
  if (views.length === 0) {
    return <EmptyState title={t("noViews")} hint={t("noViewsHint")} />;
  }
  const selected = source.selectedViewName?.() ?? null;
  const shown = q
    ? views.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          (v.sourceFile ?? "").toLowerCase().includes(q),
      )
    : views;
  return (
    <EntityList
      items={shown}
      empty={t("noViewsMatch", { query })}
      keyOf={(v) => v.id ?? `${v.space ?? ""}:${v.name}`}
      render={(v) => (
        <EntityRow
          depth={1}
          icon={<ViewIcon />}
          label={displayLabel(bareViewLabel(v.name))}
          // D5: the landed space tells twin same-named views apart; else the
          // source file locates the view.
          suffix={v.space ?? (v.sourceFile ? basename(v.sourceFile) : undefined)}
          title={v.sourceFile}
          active={selected === v.name}
          onClick={() => (source.selectView ?? source.openView)?.(v.name)}
          onDoubleClick={() => source.openView?.(v.name)}
        />
      )}
    />
  );
}

// A module + its interfaces + nested children, indented. The module row shows
// its applied type as a faint suffix, highlights when selected, and marks
// auto-named modules "derived"; interface rows read as a lighter sub-layer
// (dot + italic), anonymous ones faded with the viewer's display name.
// Externalised children (declared in a different file) carry a ↗ jump to
// their declaration-file stub.
function ModuleNode(props: {
  module: Module;
  /** Qualified path of this module in the resolved tree (`Platform.Orders`). */
  path: string;
  depth: number;
  selectedId: string | null;
  hoverStore?: HoverStore;
  /** Expanded module paths — modules default collapsed (#4). */
  expanded: ReadonlySet<string>;
  onToggleModule: (path: string) => void;
  externByPath: ReadonlyMap<string, string>;
  registerRow: (key: string) => (el: HTMLDivElement | null) => void;
  onJumpToStub: (path: string) => void;
  onModule: (m: Module) => void;
  onModuleOpen: (m: Module) => void;
  onModuleContextMenu?: (m: Module, e: ReactMouseEvent) => void;
  onInterface: (m: Module, name: string) => void;
  onInterfaceOpen: (m: Module, name: string) => void;
}): ReactElement {
  const {
    module: m,
    path,
    depth,
    selectedId,
    hoverStore,
    expanded,
    onToggleModule,
    externByPath,
    registerRow,
    onJumpToStub,
    onModule,
    onModuleOpen,
    onModuleContextMenu,
    onInterface,
    onInterfaceOpen,
  } = props;
  const { t } = useTranslation("nav");
  const key = moduleKey(m);
  const stubFile = externByPath.get(path);
  const hasContent = m.interfaces.length > 0 || m.children.length > 0;
  // Default collapsed: interfaces + nested children show only once opened.
  const open = expanded.has(path);
  return (
    <>
      <EntityRow
        depth={depth}
        icon={<ModuleIcon module={m} typeName={m.typeName} />}
        label={displayLabel(m.name)}
        suffix={m.typeName !== "module" ? m.typeName : undefined}
        active={selectedId !== null && key === selectedId}
        hoverStore={hoverStore}
        hoverId={key}
        derived={!!m.unnamed}
        expand={hasContent ? { open, onToggle: () => onToggleModule(path) } : null}
        innerRef={(el) => {
          registerRow(`mod:${key}`)(el);
          if (stubFile) registerRow(`real:${path}`)(el);
        }}
        action={
          stubFile
            ? {
                icon: "↗",
                title: t("moduleExternalisedJump", { file: basename(stubFile) }),
                onClick: () => onJumpToStub(path),
              }
            : undefined
        }
        onClick={() => onModule(m)}
        onDoubleClick={() => onModuleOpen(m)}
        onContextMenu={onModuleContextMenu ? (e) => onModuleContextMenu(m, e) : undefined}
      />
      {open &&
        m.interfaces.map((iface: Interface) => (
          <EntityRow
            key={m.name + "." + iface.name}
            depth={depth + 1}
            icon={<InterfaceIcon />}
            label={`${iface.typeName} ${displayLabel(interfaceDisplayName(iface))}`}
            italic
            faded={isAnonymousInterface(iface)}
            hoverStore={hoverStore}
            hoverId={key}
            onClick={() => onInterface(m, iface.name)}
            onDoubleClick={() => onInterfaceOpen(m, iface.name)}
          />
        ))}
      {open &&
        m.children.map((child) => (
          <ModuleNode
            key={moduleKey(child)}
            module={child}
            path={`${path}.${child.name}`}
            depth={depth + 1}
            selectedId={selectedId}
            hoverStore={hoverStore}
            expanded={expanded}
            onToggleModule={onToggleModule}
            onModuleContextMenu={onModuleContextMenu}
            externByPath={externByPath}
            registerRow={registerRow}
            onJumpToStub={onJumpToStub}
            onModule={onModule}
            onModuleOpen={onModuleOpen}
            onInterface={onInterface}
            onInterfaceOpen={onInterfaceOpen}
          />
        ))}
    </>
  );
}

// Processes tab: owner modules as a tree (default expanded), processes nested
// as leaves. Module rows behave like the files tab (type icon, select/open,
// hover link); process leaves have no diamond — context makes them processes.
function ProcessTreeRows(props: {
  nodes: readonly ProcTreeNode[];
  depth: number;
  collapsed: ReadonlySet<string>;
  selectedModuleId: string | null;
  hoverStore?: HoverStore;
  activeProcessId: string | null;
  onToggle: (key: string) => void;
  onModule: (m: Module) => void;
  onModuleOpen: (m: Module) => void;
  onProcess: (p: Process) => void;
  onProcessOpen: (p: Process) => void;
  onModuleContextMenu?: (m: Module, e: ReactMouseEvent) => void;
  onProcessContextMenu?: (p: Process, e: ReactMouseEvent) => void;
}): ReactElement {
  return (
    <>
      {props.nodes.map((node) => {
        const open = !props.collapsed.has(node.key);
        const m = node.module;
        const mKey = m ? moduleKey(m) : null;
        const childNodes = [...node.children.values()].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        return (
          <div key={node.key}>
            <EntityRow
              depth={props.depth}
              icon={<ModuleIcon module={m ?? undefined} typeName={m?.typeName ?? "module"} />}
              label={displayLabel(node.name)}
              suffix={m && m.typeName !== "module" ? m.typeName : undefined}
              active={!!mKey && props.selectedModuleId === mKey}
              hoverStore={props.hoverStore}
              hoverId={mKey ?? undefined}
              expand={{ open, onToggle: () => props.onToggle(node.key) }}
              onClick={() => m && props.onModule(m)}
              onDoubleClick={() => m && props.onModuleOpen(m)}
              onContextMenu={
                props.onModuleContextMenu && m
                  ? (e) => props.onModuleContextMenu!(m, e)
                  : undefined
              }
            />
            {open && (
              <>
                <ProcessTreeRows
                  nodes={childNodes}
                  depth={props.depth + 1}
                  collapsed={props.collapsed}
                  selectedModuleId={props.selectedModuleId}
                  hoverStore={props.hoverStore}
                  activeProcessId={props.activeProcessId}
                  onToggle={props.onToggle}
                  onModule={props.onModule}
                  onModuleOpen={props.onModuleOpen}
                  onProcess={props.onProcess}
                  onProcessOpen={props.onProcessOpen}
                  onModuleContextMenu={props.onModuleContextMenu}
                  onProcessContextMenu={props.onProcessContextMenu}
                />
                {node.procs.map((p) => (
                  <EntityRow
                    key={processKey(p)}
                    depth={props.depth + 1}
                    icon={<ProcessIcon />}
                    expand={null}
                    label={displayLabel(p.name)}
                    active={props.activeProcessId !== null && processKey(p) === props.activeProcessId}
                    onClick={() => props.onProcess(p)}
                    onDoubleClick={() => props.onProcessOpen(p)}
                    onContextMenu={
                      props.onProcessContextMenu ? (e) => props.onProcessContextMenu!(p, e) : undefined
                    }
                  />
                ))}
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

function EntityRow(props: {
  depth: number;
  /** Type/entity glyph. Omit (group headers) to render chevron → label with no
   *  icon slot — every real entity passes one, so the column stays aligned. */
  icon?: ReactNode;
  label: string;
  /** Qualified-path context shown faintly after the label (search results). */
  hint?: string;
  suffix?: string;
  muted?: boolean;
  italic?: boolean;
  /** Bold label — group headers (library / process-file) read as a heading. */
  strong?: boolean;
  /** Selection highlight (viewer parity — selected entity reads selected). */
  active?: boolean;
  /** Auto-named (unnamed) module — gets a faint "derived" chip. */
  derived?: boolean;
  /** Anonymous/unspecified entity (or an externalised stub) — fades the row. */
  faded?: boolean;
  /** Tooltip override (defaults to hint.label / label). */
  title?: string;
  /** Secondary jump affordance (externalised stub ↔ real counterpart). */
  action?: { readonly icon: string; readonly title: string; readonly onClick: () => void };
  /** Row-element ref (jump-to-counterpart / nav-reveal scroll target). */
  innerRef?: (el: HTMLDivElement | null) => void;
  /** Board↔nav hover channel + this row's board id. The row subscribes
   *  PER-ROW (so a board-hover change re-renders only the matching row, not
   *  the whole nav) and writes its id on pointer enter. Omit for rows with no
   *  board counterpart (files, dirs, types, process leaves). */
  hoverStore?: HoverStore;
  hoverId?: string;
  /** Expand chevron before the icon. Every row reserves this fixed-width cell
   *  so icons share one column regardless of depth or expandability: an object
   *  renders a working toggle; null/undefined render an empty spacer. A row
   *  without a chevron thus keeps its icon aligned with expandable siblings —
   *  and one cell right of its parent's icon. */
  expand?: { readonly open: boolean; readonly onToggle: () => void } | null;
  onClick: () => void;
  /** Universal grammar: double click = bring into view / open. */
  onDoubleClick?: () => void;
  /** Right-click / touch long-press menu (rows with board actions). */
  onContextMenu?: (e: ReactMouseEvent) => void;
}): ReactElement {
  const hovered = useNavHovered(props.hoverStore, props.hoverId);
  const store = props.hoverStore;
  const hoverId = props.hoverId;
  // mouseLeave never fires when the hovered row is REMOVED from the DOM
  // (recompile drops the entity, search filter narrows, nav view switch) —
  // clear on unmount so the linked board node doesn't keep a ghost highlight.
  useEffect(() => {
    if (!store || !hoverId) return;
    return () => {
      if (store.getHoveredId() === hoverId) store.setHoveredId(null);
    };
  }, [store, hoverId]);
  return (
    <Tooltip content={props.title ?? (props.hint ? `${props.hint}.${props.label}` : props.label)}>
    <div
      ref={props.innerRef}
      role="button"
      tabIndex={0}
      onClick={props.onClick}
      onDoubleClick={props.onDoubleClick}
      onContextMenu={props.onContextMenu}
      onMouseEnter={store && hoverId ? () => store.setHoveredId(hoverId) : undefined}
      onMouseLeave={store && hoverId ? () => store.setHoveredId(null) : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          props.onClick();
        }
      }}
      aria-current={props.active ? "true" : undefined}
      style={{ paddingLeft: 8 + props.depth * 12 }}
      className={cx(
        "flex w-full cursor-pointer items-center gap-1.5 truncate py-1 pr-2 text-left text-[13px] hover:bg-hover pointer-coarse:gap-2 pointer-coarse:py-3 pointer-coarse:text-[15px] pointer-coarse:rounded-lg pointer-coarse:active:bg-white/10",
        props.active
          ? "bg-selection text-slate-50"
          : props.muted
            ? "text-slate-400"
            : "text-slate-200",
        !props.active && hovered && "bg-hover text-white",
        props.italic && "italic",
        props.faded && "opacity-60",
      )}
    >
      {props.expand ? (
        <button
          type="button"
          aria-label={props.expand.open ? "Collapse" : "Expand"}
          aria-expanded={props.expand.open}
          onClick={(e) => {
            e.stopPropagation();
            props.expand!.onToggle();
          }}
          onDoubleClick={(e) => e.stopPropagation()}
          className="flex w-3 shrink-0 justify-center text-slate-500 pointer-coarse:w-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
        >
          <Chevron open={props.expand.open} />
        </button>
      ) : (
        <span aria-hidden="true" className="w-3 shrink-0 pointer-coarse:w-7" />
      )}
      {props.icon != null && (
        <span aria-hidden="true" className="relative top-0.5 -mr-0.5 shrink-0 text-[12px] text-slate-600 pointer-coarse:text-[13px]">{props.icon}</span>
      )}
      {props.action && (
        <Tooltip content={props.action.title}>
          <button
            type="button"
            aria-label={props.action.title}
            onClick={(e) => {
              e.stopPropagation();
              props.action!.onClick();
            }}
            onDoubleClick={(e) => e.stopPropagation()}
            className="-mx-0.5 shrink-0 rounded px-0.5 text-slate-500 hover:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            {props.action.icon}
          </button>
        </Tooltip>
      )}
      <span className={cx("truncate", props.strong && "font-medium")}>{props.label}</span>
      {props.derived && (
        <span className="shrink-0 rounded bg-slate-800 px-1 text-[9px] uppercase tracking-wide text-slate-500">
          derived
        </span>
      )}
      {props.hint && (
        <span className="min-w-0 shrink truncate text-[11px] text-slate-500">{props.hint}</span>
      )}
      {props.suffix && (
        <span className="ml-auto shrink-0 truncate pl-2 text-[11px] text-slate-500">{props.suffix}</span>
      )}
    </div>
    </Tooltip>
  );
}

// Deterministic type-name → colour: same string, same swatch, everywhere (the
// files tab's instance icons and the types tab's badges stay in sync). Named
// rather than inlined so the call sites below read as an intent, not a detail —
// this is the palette's value-stable hash, not a local one, because a nav badge
// and a board coloured by the same string must not disagree.
function typeColor(name: string): string {
  return aspectColorFor(name);
}

/** A type's own declared widget icon (`widget.icon` in its body), if any.
 *  Only the type's OWN body is inspected — inherited icons fall back to the
 *  base-kind badge, same as the board can't show an icon a type never set. */
function typeOwnIconName(type: TypeDef): string | undefined {
  for (const f of type.fields ?? []) {
    if (f.path.length === 2 && f.path[0] === "widget" && f.path[1] === "icon") {
      const s = valueToString(f.value);
      if (s) return s;
    }
  }
  return undefined;
}

/**
 * Types-tab row icon. Renders the widget glyph resolved up the type's parent
 * chain (tinted by the type-name hue) so it reads as the same symbol its
 * instances show on the board; falls back to the base-kind badge when no icon
 * resolves anywhere in the chain. `iconName` is precomputed by the caller.
 */
function TypeIcon({
  type,
  baseKind,
  iconName,
}: {
  type: TypeDef;
  baseKind: BaseType;
  iconName?: string;
}): ReactElement {
  if (iconName) {
    // No title here — the row already carries the type name; a second
    // title on the icon makes getByTitle ambiguous and adds a dupe tooltip.
    return (
      <span
        aria-hidden="true"
        style={{ color: typeColor(type.name) }}
        className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center pointer-coarse:h-[18px] pointer-coarse:w-[18px] [&_svg]:h-full [&_svg]:w-full"
        dangerouslySetInnerHTML={{ __html: iconSvg(iconName, 14) }}
      />
    );
  }
  if (baseKind === "interface") return <InterfaceIcon />;
  return (
    <BaseTypeBadge
      baseType={baseKind}
      color={baseKind === "module" ? typeColor("module") : undefined}
    />
  );
}

/** Base-kind badge per type — same mark coding as the viewer. Module-based
 *  types colour by `color` (stable-hashed from the type name) so the badge
 *  matches the instances' file-tab icons; others keep the fixed kind colour. */
function BaseTypeBadge({ baseType, color }: { baseType: BaseType; color?: string }): ReactElement {
  return (
    <span
      title={baseType}
      style={color ? { color } : undefined}
      className={cx(
        "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-xs border border-slate-500 bg-slate-800/(--tint-dim) text-[9px] font-bold leading-none pointer-coarse:h-[18px] pointer-coarse:w-[18px] pointer-coarse:text-[11px]",
        !color && BASE_TYPE_COLOR[baseType],
      )}
    >
      {BASE_TYPE_MARK[baseType]}
    </span>
  );
}

/** Read a module's resolved widget icon name (the same one the board renders),
 *  falling back to the builtin `module` glyph when the type sets none. */
function moduleIconName(module?: Module): string {
  if (!module) return "module";
  const icon = extractWidgetSpec(module).props.get("icon");
  return typeof icon === "string" && icon ? icon : "module";
}

/**
 * Module row icon — the real board widget glyph (`widget.icon`), tinted to
 * match the board: the active legend colour for this node when a view is
 * coloured, else the type's own `widget.color`, else a stable type-name hue.
 * One symbol system across the nav and the diagram.
 */
function ModuleIcon({
  module,
  typeName,
}: {
  module?: Module;
  typeName: string;
}): ReactElement {
  const coloring = useNodeColoring();
  const legendColor = module ? coloring?.colorForNode(moduleKey(module)) : undefined;
  // `widget.color` carries a palette NAME (or a literal), so it resolves the same
  // way the board's card composer resolves it — handing CSS the raw name would
  // tint this icon with the keyword while the node beside it shows the theme's.
  const named = module
    ? (extractWidgetSpec(module).props.get("color") as string | undefined)
    : undefined;
  const widgetColor = named ? paletteColor(named) : undefined;
  const color = legendColor ?? widgetColor ?? typeColor(typeName);
  const svg = iconSvg(moduleIconName(module), 14) || iconSvg("module", 14);
  return (
    <span
      title={typeName}
      style={{ color }}
      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center pointer-coarse:h-[18px] pointer-coarse:w-[18px] [&_svg]:h-full [&_svg]:w-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/** Interface icon — a plain lowercase "i" in the regular text colour (no
 *  badge box), the universal interface mark across nav rows. */
function InterfaceIcon(): ReactElement {
  return (
    <span
      aria-hidden="true"
      data-testid="interface-icon"
      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center font-serif text-[15px] not-italic leading-none text-slate-200 pointer-coarse:h-[18px] pointer-coarse:w-[18px] pointer-coarse:text-[18px]"
    >
      i
    </span>
  );
}

/** Process icon — universal squiggle glyph, soft off-white, for entity rows. */
function ProcessIcon(): ReactElement {
  return (
    <span
      aria-hidden="true"
      data-testid="process-icon"
      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center pointer-coarse:h-[18px] pointer-coarse:w-[18px]"
    >
      <ProcessGlyph className="h-3.5 w-3.5 text-slate-200 pointer-coarse:h-[18px] pointer-coarse:w-[18px]" />
    </span>
  );
}

// Recursive subtype rows for the Types tab — chevron toggles the subtree,
// clicking the row selects the type.
function TypeNodeRows(props: {
  nodes: readonly TypeNode[];
  depth: number;
  selected: string | null;
  baseTypeByName: ReadonlyMap<string, BaseType>;
  iconByName: ReadonlyMap<string, string>;
  collapsed: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onSelect: (name: string) => void;
  onOpen: (name: string) => void;
}): ReactElement {
  return (
    <>
      {props.nodes.map((node) => {
        const name = node.type.name;
        // Selection + React keys use the space-qualified selection key so two
        // spaces' same-named types stay distinct rows and picks (r56).
        const selKey = typeSelectionKey(node.type);
        const bk = props.baseTypeByName.get(name);
        const hasChildren = node.children.length > 0;
        const key = `typenode:${selKey}`;
        const open = !props.collapsed.has(key);
        const active = selKey === props.selected;
        return (
          <div key={selKey}>
            <EntityRow
              depth={props.depth}
              icon={bk ? <TypeIcon type={node.type} baseKind={bk} iconName={props.iconByName.get(name)} /> : "{}"}
              label={name}
              active={active}
              expand={hasChildren ? { open, onToggle: () => props.onToggle(key) } : null}
              onClick={() => props.onSelect(selKey)}
              onDoubleClick={() => props.onOpen(selKey)}
            />
            {hasChildren && open && (
              <TypeNodeRows
                nodes={node.children}
                depth={props.depth + 1}
                selected={props.selected}
                baseTypeByName={props.baseTypeByName}
                iconByName={props.iconByName}
                collapsed={props.collapsed}
                onToggle={props.onToggle}
                onSelect={props.onSelect}
                onOpen={props.onOpen}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
